/**
 * Demo tenant data — "Acme Bank".
 * A mature banking enterprise deployment used for demos, design partner
 * reviews, conferences and screenshots. Acme Bank is the *banking-flavoured*
 * tenant; Northwind Insurance (registered below) is the insurance-flavoured
 * tenant and lives in `lib/northwindInsuranceData.js`.
 *
 * IMPORTANT: All data structures here MUST match what the Customer-Mode
 * onboarding flow will produce. The application reads from a single shape
 * regardless of mode; only the data source differs.
 */
import { NORTHWIND_TENANT_BUNDLE } from "@/lib/northwindInsuranceData";
import { burnByBusinessUnit, vendorBurnRollup as deriveVendorBurnRollup, topBurners } from "@/lib/costRollups";

// ----- Tenant -----
export const DEMO_TENANT = {
  id: "acme-bank",
  name: "Acme Bank",
  industry: "Banking & Financial Services",
  region: "AU · Sydney",
  residency: "ap-southeast-2",
  tier: "ENTERPRISE",
  domain: "acmebank.com.au",
  contractStart: "2025-09-01",
  cmkId: "kms-acme-bank-…7e21",
};

// ----- Vendors (10) -----
export const DEMO_VENDORS = [
  // P95 latency = first-token time + average-completion time, sampled over 7d.
  // OpenAI / Azure OpenAI / Anthropic premium tier: 900-1500ms typical for
  // gpt-4o / claude-3.5-sonnet on AU-region traffic.
  { id: "openai", name: "OpenAI", category: "LLM", authMethod: "OAuth · Org", status: "healthy", agentsGoverned: 14, latencyMs: 1180, lastSync: "12s ago", monthlySpendAud: 184200 },
  { id: "anthropic", name: "Anthropic", category: "LLM", authMethod: "API Key", status: "healthy", agentsGoverned: 9, latencyMs: 1340, lastSync: "14s ago", monthlySpendAud: 96400 },
  { id: "bedrock", name: "AWS Bedrock", category: "LLM Gateway", authMethod: "IAM Role", status: "healthy", agentsGoverned: 11, latencyMs: 1420, lastSync: "18s ago", monthlySpendAud: 152800 },
  { id: "vertex", name: "Google Vertex AI", category: "LLM Gateway", authMethod: "Service Acct", status: "degraded", agentsGoverned: 4, latencyMs: 2680, lastSync: "02m ago", monthlySpendAud: 38400 },
  { id: "azure-oai", name: "Azure OpenAI", category: "LLM", authMethod: "Managed Id", status: "healthy", agentsGoverned: 6, latencyMs: 1090, lastSync: "20s ago", monthlySpendAud: 71200 },
  { id: "watsonx", name: "IBM watsonx", category: "LLM", authMethod: "API Key", status: "healthy", agentsGoverned: 2, latencyMs: 1610, lastSync: "44s ago", monthlySpendAud: 24800 },
  { id: "copilot", name: "Microsoft Copilot", category: "Productivity AI", authMethod: "M365 Audit", status: "healthy", agentsGoverned: 1, latencyMs: 780, lastSync: "01m ago", monthlySpendAud: 18600 },
  { id: "gh-copilot", name: "GitHub Copilot", category: "Code AI", authMethod: "Org OAuth", status: "healthy", agentsGoverned: 1, latencyMs: 640, lastSync: "32s ago", monthlySpendAud: 12400 },
  { id: "mistral", name: "Mistral AI", category: "LLM", authMethod: "API Key", status: "healthy", agentsGoverned: 1, latencyMs: 1240, lastSync: "55s ago", monthlySpendAud: 6800 },
  { id: "cohere", name: "Cohere", category: "LLM", authMethod: "API Key", status: "healthy", agentsGoverned: 1, latencyMs: 970, lastSync: "01m ago", monthlySpendAud: 5200 },
];

// ----- Departments -----
const DEPARTMENTS = ["Lending", "Risk & Compliance", "Cards", "Operations", "Customer Service", "Capital Markets", "Wealth Mgmt", "Treasury", "HR", "Marketing"];
const OWNERS = ["J. Patel", "M. Tan", "S. Nair", "P. Ravi", "W. Soo", "A. Wright", "K. Hassan", "R. Chen", "L. Okafor", "T. Berg", "N. Kapoor", "F. Dubois"];
const AUTONOMY = ["Suggest", "Recommend", "Act"];
const STATUSES = ["live", "live", "live", "live", "monitored", "retired"];
const MODELS_BY_VENDOR = {
  openai: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo"],
  anthropic: ["claude-3.5-sonnet", "claude-3-haiku", "claude-3-opus"],
  bedrock: ["claude-3-sonnet", "llama-3.1-70b", "titan-express"],
  vertex: ["gemini-1.5-pro", "gemini-1.5-flash"],
  "azure-oai": ["gpt-4o", "gpt-4o-mini"],
  watsonx: ["granite-13b", "granite-3-instruct"],
  copilot: ["m365-copilot"],
  "gh-copilot": ["copilot-chat"],
  mistral: ["mistral-large", "mistral-small"],
  cohere: ["command-r-plus", "command-r"],
};

// ----- Agents (50 governed + 3 shadow) -----
// Each agent carries the canonical inventory fields (S12) **and** the
// extended fields that drive the Agent Detail surface (S13) —
// sub-risk breakdown, applied policies, drift history, lineage, version
// history, linked deployment artefacts. All values are deterministic so
// screenshots and tests stay stable.

const AGENT_POLICY_POOL = ["POL-BANK-101", "POL-BANK-102", "POL-BANK-103", "POL-BANK-104", "POL-BANK-105", "POL-BANK-106", "POL-BANK-107", "POL-BANK-108"];
const AGENT_DATA_SOURCES = [
  "Core Banking Ledger (Murex)", "CRM (Salesforce)", "Loan Origination (nCino)",
  "Customer KYC Vault", "Risk Data Warehouse (Snowflake)", "Card Processing (FIS)",
  "Document Store (SharePoint)", "Treasury Position Store", "Trade Surveillance Feed",
  "Marketing CDP", "HR · Workday", "Vendor Risk Register",
];
const AGENT_FRAMEWORKS = [
  "APRA CPS 234", "ASIC RG 271", "Privacy Act AU", "EU AI Act", "ISO 42001", "NIST AI RMF",
];

// Deterministic 12-week mini series in [min, max]
function driftSeries(seed, min, max, trend = 0) {
  const out = [];
  let v = min + ((seed * 31) % (max - min));
  for (let i = 0; i < 12; i++) {
    const wobble = ((seed * (i + 7) * 17) % 11) - 5;
    v = Math.max(min, Math.min(max, v + wobble + trend));
    out.push(Math.round(v * 10) / 10);
  }
  return out;
}

// ----- 14 named banking AI agents (governed) + 3 shadow AI tools -----
// Hand-crafted, industry-realistic. Each agent has a real banking purpose,
// a named owner, a vendor + model, and full risk / drift / lineage metadata.

// Blended cost per 1k tokens (AUD) — input + output mix, 2026 enterprise
// list rates. Used to display "$X per 1k tok" on each agent card.
const BLENDED_K_TOK_COST = {
  "claude-3.5-sonnet": 0.018,
  "claude-3-sonnet":   0.014,
  "claude-3-haiku":    0.0012,
  "gpt-4o":            0.012,
  "gpt-4o-mini":       0.0008,
  "mistral-large":     0.010,
  "gemini-1.5-pro":    0.011,
  "gemini-1.5-flash":  0.0010,
  "granite-13b":       0.004,
  "copilot":           0.006,
};
const blendedK = (model) => BLENDED_K_TOK_COST[model] ?? 0.008;

const ACME_AGENTS_RAW = [
  // Monthly cost recalibrated 11 Feb 2026 — sums to A$610k/mo to match the
  // vendor + department rollups. Highest spenders are 24/7 customer-facing
  // (Contact Centre Copilot, AML Triage, Vulnerable Customer); cheapest are
  // batch / quarterly (Capital Adequacy, Model Risk Doc).
  { id: "AGT-BANK-001", name: "AML Alert Triage Agent",                 purpose: "Disposition Tier-2 AML alerts: enrich, score and recommend close vs investigate with rationale", dept: "AML / CTF",             bu: "Financial Crime",   owner: "S. Nair",   vendor: "anthropic", model: "claude-3.5-sonnet", autonomy: "Recommend", risk: 88, drift: "warning", status: "monitored", cost:  98000, ret: 4.0 },
  { id: "AGT-BANK-002", name: "Credit Risk Assessment Assistant",       purpose: "Summarise applicant financials and policy exceptions for the credit decisioner workbench",      dept: "Credit Risk",          bu: "Business Banking",  owner: "A. Wright", vendor: "openai",    model: "gpt-4o",            autonomy: "Suggest",   risk: 82, drift: "stable",  status: "live",      cost:  48000, ret: 3.4 },
  { id: "AGT-BANK-003", name: "KYC Review Copilot",                     purpose: "Extract and verify identity / address / source-of-funds evidence from KYC document packs",       dept: "KYC / Onboarding",     bu: "Operations",        owner: "M. Tan",    vendor: "azure-oai", model: "gpt-4o-mini",       autonomy: "Recommend", risk: 64, drift: "stable",  status: "live",      cost:  28000, ret: 3.8 },
  { id: "AGT-BANK-004", name: "Mortgage Document Extraction Agent",     purpose: "Classify and extract structured fields (payslips, BAS, contracts) from mortgage application PDFs", dept: "Mortgage Operations", bu: "Retail Banking",   owner: "A. Wright", vendor: "bedrock",   model: "claude-3-sonnet",   autonomy: "Act",       risk: 46, drift: "stable",  status: "live",      cost:  36000, ret: 4.2 },
  { id: "AGT-BANK-005", name: "Fraud Case Summarisation Agent",         purpose: "Assemble investigator-ready fraud case packs with citations to transactions, comms and KYC",      dept: "Fraud Operations",     bu: "Financial Crime",   owner: "K. Hassan", vendor: "anthropic", model: "claude-3.5-sonnet", autonomy: "Suggest",   risk: 71, drift: "stable",  status: "live",      cost:  42000, ret: 3.6 },
  { id: "AGT-BANK-006", name: "Collections Prioritisation Agent",       purpose: "Prioritise the daily collections queue by hardship signals, balance, and contact propensity",     dept: "Collections",          bu: "Retail Banking",    owner: "J. Patel",  vendor: "openai",    model: "gpt-4o-mini",       autonomy: "Recommend", risk: 58, drift: "stable",  status: "live",      cost:  18000, ret: 3.2 },
  { id: "AGT-BANK-007", name: "Vulnerable Customer Detection Agent",    purpose: "Detect hardship / vulnerability signals in inbound calls and chats; route to priority support",   dept: "Customer Service",     bu: "Retail Banking",    owner: "A. Wright", vendor: "openai",    model: "gpt-4o",            autonomy: "Suggest",   risk: 84, drift: "warning", status: "live",      cost:  62000, ret: 2.8 },
  { id: "AGT-BANK-008", name: "Complaints Insights Agent",              purpose: "Weekly LLM clustering of customer complaints by theme + conduct-risk signal surfacing",          dept: "Risk & Compliance",    bu: "Operations",        owner: "P. Ravi",   vendor: "openai",    model: "gpt-4o-mini",       autonomy: "Suggest",   risk: 42, drift: "stable",  status: "live",      cost:  14000, ret: 3.0 },
  { id: "AGT-BANK-009", name: "Regulatory Reporting QA Agent",          purpose: "Quality-check APRA / ASIC / AUSTRAC quarterly returns against source data before submission",     dept: "Risk & Compliance",    bu: "Risk & Compliance", owner: "S. Nair",   vendor: "anthropic", model: "claude-3-haiku",    autonomy: "Suggest",   risk: 56, drift: "stable",  status: "live",      cost:  22000, ret: 2.9 },
  { id: "AGT-BANK-010", name: "Contact Centre Copilot",                 purpose: "Real-time citation-grounded responses for retail contact-centre agents in customer conversations",dept: "Customer Service",     bu: "Retail Banking",    owner: "J. Patel",  vendor: "openai",    model: "gpt-4o",            autonomy: "Suggest",   risk: 62, drift: "stable",  status: "live",      cost: 138000, ret: 3.1 },
  { id: "AGT-BANK-011", name: "Payments Anomaly Detection Agent",       purpose: "Score outbound payment instructions for anomalies (size, geography, beneficiary, time)",          dept: "Payments",             bu: "Operations",        owner: "M. Tan",    vendor: "anthropic", model: "claude-3.5-sonnet", autonomy: "Recommend", risk: 79, drift: "warning", status: "live",      cost:  58000, ret: 3.5 },
  { id: "AGT-BANK-012", name: "Model Risk Documentation Assistant",     purpose: "Draft the model card sections (intent, lineage, limitations) for the MRM committee submission",   dept: "Model Risk Management",bu: "Risk & Compliance", owner: "W. Soo",    vendor: "openai",    model: "gpt-4o-mini",       autonomy: "Act",       risk: 38, drift: "stable",  status: "live",      cost:  14000, ret: 3.4 },
  { id: "AGT-BANK-013", name: "Treasury Narrative Drafter",             purpose: "Draft daily cash-flow and liquidity commentary for Treasury management reports",                  dept: "Treasury",             bu: "Treasury",          owner: "J. Patel",  vendor: "openai",    model: "gpt-4o",            autonomy: "Suggest",   risk: 32, drift: "stable",  status: "live",      cost:  18000, ret: 2.6 },
  { id: "AGT-BANK-014", name: "Capital Adequacy Commentary Agent",      purpose: "Auto-draft capital adequacy narrative (CET1, leverage, LCR) for the quarterly APRA submission",   dept: "Treasury",             bu: "Treasury",          owner: "J. Patel",  vendor: "anthropic", model: "claude-3-haiku",    autonomy: "Suggest",   risk: 48, drift: "stable",  status: "live",      cost:  14000, ret: 2.5 },
];

// First 6 named agents cross-link to the 6 existing deployment requests
// and a representative opportunity so drawer drill-downs always resolve.
const ACME_LINK_REQ = ["DR-BANK-0421", "DR-BANK-0420", "DR-BANK-0419", "DR-BANK-0418", "DR-BANK-0417", "DR-BANK-0416"];
const ACME_LINK_OPP = ["OPP-BANK-2000", "OPP-BANK-2001", "OPP-BANK-2002", "OPP-BANK-2003", "OPP-BANK-2006", "OPP-BANK-2005"];

export const DEMO_AGENTS = (() => {
  const out = ACME_AGENTS_RAW.map((a, i) => {
    const riskBand = a.risk < 45 ? "low" : a.risk < 70 ? "med" : "high";
    const policyIds = ["POL-BANK-102", "POL-BANK-103"]; // PII Masking + Vendor Allow-list — every agent
    if (a.autonomy !== "Suggest") policyIds.push("POL-BANK-104");
    if (riskBand !== "low") policyIds.push("POL-BANK-101", "POL-BANK-105");
    if (riskBand === "high") policyIds.push("POL-BANK-107");
    policyIds.push("POL-BANK-106"); // Cost cap on everyone
    policyIds.push("POL-BANK-108"); // Drift threshold on everyone

    return {
      id: a.id,
      name: a.name,
      purpose: a.purpose,
      vendorId: a.vendor,
      vendorName: DEMO_VENDORS.find((v) => v.id === a.vendor)?.name || a.vendor,
      model: a.model,
      version: `v1.${(i % 8) + 1}.${(i % 4) + 1}`,
      ownerName: a.owner,
      department: a.dept,
      businessUnit: a.bu,
      autonomy: a.autonomy,
      riskScore: a.risk,
      riskBand,
      status: a.status,
      agentType: "governed",
      monthlyCostAud: a.cost,
      monthlyValueAud: Math.round(a.cost * a.ret),
      per1kToken: blendedK(a.model),
      deployedAt: `2025-${String(((i % 10) + 3)).padStart(2, "0")}-${String(((i * 3) % 27) + 1).padStart(2, "0")}`,
      driftSignal: a.drift,
      incidentsOpen: i === 0 || i === 4 ? 1 : 0,
      policiesApplied: policyIds.length,
      subRiskScores: {
        dataSensitivity:    Math.min(99, a.risk + (((i * 7) % 14) - 7)),
        autonomy:           a.autonomy === "Act" ? 78 : a.autonomy === "Recommend" ? 52 : 28,
        regulatoryExposure: Math.min(99, a.risk - 4 + ((i * 11) % 12)),
        drift:              a.drift === "alert" ? 78 : a.drift === "warning" ? 58 : 22,
      },
      policyIds,
      framework: AGENT_FRAMEWORKS[i % AGENT_FRAMEWORKS.length],
      dataSources: [
        AGENT_DATA_SOURCES[i % AGENT_DATA_SOURCES.length],
        AGENT_DATA_SOURCES[(i + 4) % AGENT_DATA_SOURCES.length],
        AGENT_DATA_SOURCES[(i + 7) % AGENT_DATA_SOURCES.length],
      ],
      driftHistory: {
        quality:           driftSeries(i + 1, 78, 95, a.drift === "alert" ? -0.4 : 0),
        latencyMs:         driftSeries(i + 3, 140, 380, a.drift === "alert" ? 4 : 0),
        hallucinationRate: driftSeries(i + 5, 1, 8, a.drift === "alert" ? 0.2 : 0),
      },
      linkedRequestId: i < 6 ? ACME_LINK_REQ[i] : null,
      linkedOpportunityId: i < 6 ? ACME_LINK_OPP[i] : null,
      versionHistory: [
        { version: `v1.${(i % 8) + 1}.${(i % 4) + 1}`, releasedAt: `2026-0${(i % 3) + 1}-${((i % 27) + 1).toString().padStart(2, "0")}`, change: "Prompt tuning + safety filter v3",      actor: a.owner },
        { version: `v1.${(i % 8)}.0`,                  releasedAt: `2025-1${(i % 2) + 1}-${((i % 25) + 1).toString().padStart(2, "0")}`, change: "Model upgrade — regression test passed", actor: "engineering" },
        { version: `v1.0.0`,                            releasedAt: `2025-${String(((i % 10) + 3)).padStart(2, "0")}-${String(((i * 3) % 27) + 1).padStart(2, "0")}`, change: "Initial production deployment", actor: a.owner },
      ],
    };
  });

  // 3 shadow AI rows — surfaced in the Shadow AI tab on the Agent Inventory
  const SHADOW = [
    { id: "SHADOW-BANK-001", name: "ChatGPT (consumer)", purpose: "Lending team — drafting customer hardship letters outside the governed copilot",
      vendorId: "openai-consumer", vendorName: "ChatGPT (consumer plan)", model: "gpt-4",
      ownerName: "—", department: "Lending", autonomy: "Suggest", riskScore: 88, riskBand: "shadow",
      status: "ungoverned", agentType: "shadow", firstSeen: "12 Mar 2026 14:08 AEDT", usersDetected: 6,
      monthlyCostAud: 0, monthlyValueAud: 0, per1kToken: 0, deployedAt: "—",
      driftSignal: "—", incidentsOpen: 0, policiesApplied: 0,
      detectedVia: "Egress proxy · openai.com sessions outside SSO", evidenceCount: 14,
    },
    { id: "SHADOW-BANK-002", name: "Claude Web", purpose: "Legal & Compliance — contract redline assistance using a personal account",
      vendorId: "anthropic-web", vendorName: "Claude (web app)", model: "claude-3.5",
      ownerName: "—", department: "Legal & Compliance", autonomy: "Suggest", riskScore: 80, riskBand: "shadow",
      status: "ungoverned", agentType: "shadow", firstSeen: "08 Mar 2026 11:42 AEDT", usersDetected: 3,
      monthlyCostAud: 0, monthlyValueAud: 0, per1kToken: 0, deployedAt: "—",
      driftSignal: "—", incidentsOpen: 0, policiesApplied: 0,
      detectedVia: "Browser extension telemetry", evidenceCount: 8,
    },
    { id: "SHADOW-BANK-003", name: "Copilot Personal", purpose: "Business Banking — proposal drafting via personal M365 account",
      vendorId: "copilot-personal", vendorName: "Microsoft Copilot (personal)", model: "copilot",
      ownerName: "—", department: "Business Banking", autonomy: "Suggest", riskScore: 76, riskBand: "shadow",
      status: "ungoverned", agentType: "shadow", firstSeen: "05 Mar 2026 09:30 AEDT", usersDetected: 4,
      monthlyCostAud: 0, monthlyValueAud: 0, per1kToken: 0, deployedAt: "—",
      driftSignal: "—", incidentsOpen: 0, policiesApplied: 0,
      detectedVia: "M365 audit · personal-account usage", evidenceCount: 11,
    },
  ];
  out.push(...SHADOW);
  return out;
})();

// Silence unused-warning — the pool is exported via the agent records
// themselves (each agent carries its policyIds). The constant remains
// here for clarity and future filter UIs.
export const AGENT_POLICY_LIBRARY = AGENT_POLICY_POOL;

// ----- Opportunities (20) -----
const OPP_TITLES = [
  "Reduce mortgage approval cycle from 4d → 1d",
  "Fraud triage co-pilot for Tier-1 cases",
  "Dispute case summarisation for ops",
  "KYC document classification at intake",
  "Hardship case prioritisation",
  "AML alert disposition assistant",
  "Treasury cash-flow narrative",
  "Credit memo first-draft generator",
  "Wealth client meeting prep",
  "Card fraud signal aggregator",
  "Disclosure pack review accelerator",
  "Vendor due-diligence summariser",
  "Outage incident write-up assistant",
  "Customer hardship empathy review",
  "Marketing copy compliance check",
  "Capital adequacy commentary draft",
  "Loan policy exception explainer",
  "Branch coaching transcript analysis",
  "Investment research synthesiser",
  "Board-pack narrative drafter",
];
const EFFORT = ["S", "M", "L"];
const OPP_STATUSES = ["new", "endorsed", "in-review", "in-flight", "live"];
const BUSINESS_UNITS = [
  "Retail Banking",
  "Risk & Audit",
  "Cards & Lending",
  "Operations Centre",
  "Customer Experience",
  "Markets",
  "Wealth & Private",
  "Treasury & Liquidity",
  "People & Culture",
  "Brand & Marketing",
];
export const OPPORTUNITY_BUSINESS_UNITS = BUSINESS_UNITS;
export const OPPORTUNITY_DEPARTMENTS = DEPARTMENTS;
export const OPPORTUNITY_EFFORT_OPTIONS = [
  { code: "S", label: "Small (≤ 6 weeks · ≤ A$150k)" },
  { code: "M", label: "Medium (6–14 weeks · A$150–500k)" },
  { code: "L", label: "Large (14+ weeks · A$500k+)" },
];
export const OPPORTUNITY_RISK_OPTIONS = [
  { code: "low", label: "Low — internal use, no customer impact" },
  { code: "med", label: "Medium — customer-facing rationale" },
  { code: "high", label: "High — regulated decisioning" },
];
export const OPPORTUNITY_STATUSES = ["new", "endorsed", "in-review", "in-flight", "live"];
const AI_APPROACHES = [
  { code: "rag", label: "Retrieval-Augmented LLM", tech: ["LLM", "Vector DB", "Document parser"] },
  { code: "agent", label: "Multi-step Agent", tech: ["LLM", "Tool-use API", "Memory store"] },
  { code: "copilot", label: "Inline Copilot", tech: ["LLM", "Workspace SDK"] },
  { code: "classifier", label: "Classifier + LLM rationale", tech: ["Fine-tuned classifier", "LLM"] },
  { code: "summariser", label: "Summarisation pipeline", tech: ["LLM", "Chunking", "Citation extraction"] },
  { code: "scoring", label: "Scoring + Human Review", tech: ["LLM", "HITL queue"] },
];
export const OPPORTUNITY_AI_APPROACHES = AI_APPROACHES;
const COMPLIANCE_IMPACT = [
  { level: "low", note: "Internal use · no customer-facing decisioning" },
  { level: "med", note: "Customer-facing rationale — APRA CPS 234 disclosure required" },
  { level: "high", note: "Decision support on regulated product — ASIC RG 271 + APRA CPS 234" },
  { level: "high", note: "Sensitive PII — Privacy Act AU + APP 11 controls" },
  { level: "med", note: "Marketing copy review — ACCC misleading-conduct safe-harbour" },
];
const SUCCESS_METRICS_BY_TITLE = (i) => [
  "Cycle time reduction ≥ 60%",
  "Customer NPS improvement ≥ +8",
  ["≥ 25%", "≥ 40%", "≥ 60%"][i % 3] + " faster handling",
  "Compliance findings ≤ 1 per quarter",
];

// Deterministic but varied score generators.
const rng = (seed) => ((seed * 9301 + 49297) % 233280) / 233280;
const score = (i, base, spread) => Math.round(base + rng(i + 1) * spread);

export const DEMO_OPPORTUNITIES = OPP_TITLES.map((title, i) => {
  const base = 240_000;
  const wave = Math.round(380_000 * (i * 0.62)) + ((i * 91_117) % 1_200_000);
  const estAnnualRoiAud = base + wave;
  const effortCode = EFFORT[i % EFFORT.length];
  const effortScore = effortCode === "S" ? 25 : effortCode === "M" ? 55 : 82;
  const riskBand = ["low", "med", "high"][i % 3];
  const riskScore = score(i * 3, riskBand === "low" ? 20 : riskBand === "med" ? 50 : 75, 12);
  const roiScore = Math.min(99, Math.round((estAnnualRoiAud / 60_000)));
  // Priority = ROI weight 0.5 + (100−Risk) 0.3 + (100−Effort) 0.2
  const priorityScore = Math.round(roiScore * 0.5 + (100 - riskScore) * 0.3 + (100 - effortScore) * 0.2);
  const status = OPP_STATUSES[i % OPP_STATUSES.length];
  const approach = AI_APPROACHES[i % AI_APPROACHES.length];
  const compliance = COMPLIANCE_IMPACT[i % COMPLIANCE_IMPACT.length];
  const monthlySpend = Math.round(estAnnualRoiAud * (0.18 + rng(i) * 0.12));
  const paybackMonths = Math.max(2, Math.round(((monthlySpend * 4) / (estAnnualRoiAud / 12)) * 1.4));
  const timelineWeeks = effortCode === "S" ? 6 : effortCode === "M" ? 12 : 22;

  return {
    id: `OPP-BANK-${String(2000 + i).padStart(4, "0")}`,
    rank: i + 1,
    title,
    summary:
      "Process mining + recent CSAT signals indicate measurable ROI within 90 days.",
    department: DEPARTMENTS[i % DEPARTMENTS.length],
    businessUnit: BUSINESS_UNITS[i % BUSINESS_UNITS.length],
    owner: OWNERS[i % OWNERS.length],
    status,
    estAnnualRoiAud,
    estimatedValueAud: estAnnualRoiAud, // alias for table view
    estimatedInvestmentAud: monthlySpend,
    paybackMonths,
    timelineWeeks,
    effort: effortCode,
    effortScore,
    risk: riskBand,
    riskScore,
    roiScore,
    priorityScore,
    complianceImpact: compliance.level,
    complianceNote: compliance.note,
    recommendedApproach: approach.label,
    recommendedTech: approach.tech,
    sparkline: Array.from({ length: 8 }, (_, k) => 30 + ((i + k * 7) % 70)),

    // S07 — drawer narrative fields
    businessProblem:
      i % 4 === 0
        ? "Manual review across multiple systems creates a 4-day cycle; customers churn before approval."
        : i % 4 === 1
          ? "Investigators spend 60–70% of time on intake, classification and evidence assembly rather than judgement."
          : i % 4 === 2
            ? "Ops analysts re-read 40+ page case files before drafting case summaries — high cognitive load, inconsistent output."
            : "High-volume routine narratives consume senior analyst capacity that could be redirected to advisory work.",
    proposedSolution:
      `${approach.label} integrated with existing systems-of-record, surfaced inside current workflow tooling. Human-in-the-loop on high-risk decisions; automated handling for low-risk cohort.`,
    riskAssessment:
      riskBand === "high"
        ? "Decisions inform regulated outcomes. Requires CAIO + Legal sign-off, full audit chain, and quarterly model review."
        : riskBand === "med"
          ? "Customer-impacting outputs require disclosure language review and citation grounding."
          : "Internal-only assistive output. Standard governance suffices.",
    complianceConsiderations: compliance.note,
    requiredApprovals:
      riskBand === "high"
        ? ["IT Security", "Legal & Compliance", "CAIO", "Board Risk Sub-Committee"]
        : riskBand === "med"
          ? ["IT Security", "Legal & Compliance", "CAIO"]
          : ["IT Security", "CAIO"],
    successMetrics: SUCCESS_METRICS_BY_TITLE(i),
  };
});

// ----- Policies -----
// Each policy carries the canonical inventory fields **and** the
// extended fields that drive the S14 Policy Detail drawer — intent,
// enforcement rules, scope, frameworks, last violation, version log.
export const DEMO_POLICIES = [
  {
    id: "POL-BANK-101", name: "AU Data Residency", category: "Privacy", appliesTo: 11,
    status: "active", violations: 2, severity: "high", lastUpdated: "12 Mar 2026", owner: "S. Nair",
    intent: "Customer data and AI prompts must remain inside Australian sovereign infrastructure to satisfy APRA CPS 234 and Privacy Act AU obligations.",
    rules: [
      "All inference endpoints must run in ap-southeast-2 or AU-sovereign regions.",
      "Vector indexes hydrated from customer data must encrypt at rest with CMK in AU region.",
      "Cross-region replication of prompts/completions is denied at the network layer.",
    ],
    scopeText: "All agents that ingest customer PII or transaction records.",
    frameworks: ["APRA CPS 234", "APRA CPS 234", "Privacy Act AU"],
    lastViolation: { ts: "11 Mar 2026 09:14", agentId: "AGT-BANK-007", summary: "Vulnerable Customer Detection Agent — embedding request routed via us-east-1, auto-blocked, retried in ap-southeast-2." },
    versionHistory: [
      { version: "v3", ts: "12 Mar 2026", change: "Added vector index residency clause", actor: "S. Nair" },
      { version: "v2", ts: "08 Jan 2026", change: "Tightened cross-region replication rule", actor: "S. Nair" },
      { version: "v1", ts: "04 Oct 2025", change: "Initial policy", actor: "S. Nair" },
    ],
  },
  {
    id: "POL-BANK-102", name: "PII Masking in Prompts", category: "Privacy", appliesTo: 14,
    status: "active", violations: 0, severity: "high", lastUpdated: "03 Mar 2026", owner: "S. Nair",
    intent: "Prevent direct PII (names, TFN, account numbers, addresses) from reaching third-party LLM providers in raw form.",
    rules: [
      "Tokenise customer identifiers before prompt construction.",
      "Reject prompts where the PII detector confidence > 0.85.",
      "Log redaction events to the audit chain for every request.",
    ],
    scopeText: "Every agent invoking an external LLM vendor.",
    frameworks: ["Privacy Act AU", "ISO 42001"],
    lastViolation: null,
    versionHistory: [
      { version: "v2", ts: "03 Mar 2026", change: "Confidence threshold tightened 0.75 → 0.85", actor: "S. Nair" },
      { version: "v1", ts: "12 Nov 2025", change: "Initial policy", actor: "S. Nair" },
    ],
  },
  {
    id: "POL-BANK-103", name: "LLM Vendor Allow-list", category: "Vendor", appliesTo: 14,
    status: "active", violations: 1, severity: "med", lastUpdated: "28 Feb 2026", owner: "K. Hassan",
    intent: "Only LLM vendors with an executed enterprise MSA, completed vendor risk assessment, and AU residency capability may be invoked.",
    rules: [
      "Allow-list: OpenAI Enterprise, Anthropic Enterprise, Google Vertex AI, AWS Bedrock, Microsoft Copilot M365, Mistral Enterprise, IBM watsonx.",
      "Reject every non-allow-listed FQDN at the SSE / egress proxy layer.",
      "Quarterly review of the allow-list signed off by IT Security + Legal.",
    ],
    scopeText: "All AI traffic across all departments.",
    frameworks: ["APRA CPS 234", "ISO 42001"],
    lastViolation: { ts: "12 Mar 2026 11:30", agentId: "AGT-BANK-011", summary: "Payments Anomaly Detection Agent — outbound call to non-allow-listed vendor blocked, see INC-BANK-0040." },
    versionHistory: [
      { version: "v5", ts: "28 Feb 2026", change: "Added Mistral Enterprise after vendor risk pass", actor: "K. Hassan" },
      { version: "v4", ts: "10 Dec 2025", change: "Added watsonx", actor: "K. Hassan" },
      { version: "v3", ts: "01 Sep 2025", change: "Removed consumer-tier providers", actor: "K. Hassan" },
    ],
  },
  {
    id: "POL-BANK-104", name: "Autonomy Cap — Recommend", category: "Risk", appliesTo: 5,
    status: "draft", violations: 0, severity: "med", lastUpdated: "08 Mar 2026", owner: "J. Patel",
    intent: "High-risk and regulated workflows may not run at Act autonomy until a Board Risk Sub-Committee approval is on file.",
    rules: [
      "If agent.riskBand ∈ {high} and agent.autonomy === 'Act' → block.",
      "Recommended autonomy: 'Recommend' with human-in-loop sign-off.",
      "Exceptions require a stage-4 Board sign-off (see S11 governance workflow).",
    ],
    scopeText: "All agents with risk score ≥ 70 or category Risk & Compliance.",
    frameworks: ["EU AI Act", "ISO 42001"],
    lastViolation: null,
    versionHistory: [
      { version: "v1 (draft)", ts: "08 Mar 2026", change: "Authored — under review with CRO", actor: "J. Patel" },
    ],
  },
  {
    id: "POL-BANK-105", name: "Output PII Filter", category: "Privacy", appliesTo: 11,
    status: "active", violations: 0, severity: "high", lastUpdated: "20 Feb 2026", owner: "P. Ravi",
    intent: "Ensure model responses surfaced to end users do not leak other customers' PII or sensitive cross-account data.",
    rules: [
      "Apply PII-detection scan on every completion before display.",
      "If PII detected from a different customer record → redact + log incident.",
      "Auditors can replay the original response under break-glass approval.",
    ],
    scopeText: "Customer-facing agents and any agent producing externally-visible artefacts.",
    frameworks: ["Privacy Act AU", "ASIC RG 271"],
    lastViolation: null,
    versionHistory: [
      { version: "v2", ts: "20 Feb 2026", change: "Added cross-customer leakage clause", actor: "P. Ravi" },
      { version: "v1", ts: "05 Aug 2025", change: "Initial policy", actor: "P. Ravi" },
    ],
  },
  {
    id: "POL-BANK-106", name: "Cost Cap — $10k/mo", category: "Financial", appliesTo: 14,
    status: "active", violations: 3, severity: "med", lastUpdated: "01 Mar 2026", owner: "M. Tan",
    intent: "Cap per-agent monthly LLM spend to keep the AI portfolio inside FinOps envelope and prevent runaway costs.",
    rules: [
      "Hard cap A$10,000 per agent per month — auto-throttle at 90%.",
      "Open an incident at 110% of cap (see INC-BANK-0043 pattern).",
      "Cap can be increased via FinOps review with CFO endorsement.",
    ],
    scopeText: "Every governed agent — caps adjust by tier (S/M/L).",
    frameworks: ["ISO 42001"],
    lastViolation: { ts: "13 Mar 2026 09:00", agentId: "AGT-BANK-010", summary: "Contact Centre Copilot — cost spike 30% WoW, throttled, see INC-BANK-0043." },
    versionHistory: [
      { version: "v2", ts: "01 Mar 2026", change: "Added per-tier cap matrix", actor: "M. Tan" },
      { version: "v1", ts: "15 Sep 2025", change: "Initial cost-cap policy", actor: "M. Tan" },
    ],
  },
  {
    id: "POL-BANK-107", name: "Sensitive Data Scope", category: "Privacy", appliesTo: 5,
    status: "active", violations: 0, severity: "high", lastUpdated: "22 Feb 2026", owner: "P. Ravi",
    intent: "Define and enforce the precise scope of sensitive customer data each agent may access — least-privilege by default.",
    rules: [
      "Each agent must declare its data classification before stage-4 approval.",
      "Vector retrieval must be filtered by the agent's declared scope.",
      "Quarterly scope re-attestation by the agent owner.",
    ],
    scopeText: "All high-risk agents and agents touching customer PII.",
    frameworks: ["APRA CPS 234", "Privacy Act AU"],
    lastViolation: null,
    versionHistory: [
      { version: "v3", ts: "22 Feb 2026", change: "Added quarterly re-attestation requirement", actor: "P. Ravi" },
      { version: "v2", ts: "10 Dec 2025", change: "Tightened vector retrieval filter", actor: "P. Ravi" },
      { version: "v1", ts: "07 Jul 2025", change: "Initial policy", actor: "P. Ravi" },
    ],
  },
  {
    id: "POL-BANK-108", name: "Model Drift Threshold", category: "Quality", appliesTo: 14,
    status: "active", violations: 1, severity: "med", lastUpdated: "10 Mar 2026", owner: "W. Soo",
    intent: "Detect and remediate model performance drift early — answer quality, latency, hallucination — before users experience degradation.",
    rules: [
      "Answer quality < 85% over 7 days → drift warning.",
      "Hallucination rate > 5% over 7 days → drift alert + page on-call.",
      "P95 latency growing > 25% week-over-week → engineering ticket.",
    ],
    scopeText: "Every governed agent — thresholds tuned per agent tier.",
    frameworks: ["ISO 42001", "NIST AI RMF"],
    lastViolation: { ts: "08 Mar 2026 16:20", agentId: "AGT-BANK-005", summary: "Fraud Case Summarisation Agent — accuracy drift detected, see INC-BANK-0042 (now closed)." },
    versionHistory: [
      { version: "v2", ts: "10 Mar 2026", change: "Added latency growth rule", actor: "W. Soo" },
      { version: "v1", ts: "05 Nov 2025", change: "Initial drift thresholds", actor: "W. Soo" },
    ],
  },
];

// ----- Incidents -----
// Each incident drives the Incident Register (S15). `agentName` and
// `policyId` make cross-link drill-down deterministic; `narrative` is
// the human-readable timeline shown in the drawer.
export const DEMO_INCIDENTS = [
  {
    id: "INC-BANK-0044",
    title: "PII detected in agent response",
    agentId: "AGT-BANK-007",
    agentName: "Vulnerable Customer Detection Agent",
    policyId: "POL-BANK-105",
    severity: "high",
    status: "open",
    days: 3,
    owner: "Sasha Nair",
    ownerRole: "CRO",
    reported: "11 Mar 2026 09:14 AEDT",
    category: "Privacy",
    impact: "Possible APP 11.1 breach — full name + DOB surfaced in branch advisor preview before suppression.",
    narrative: "Vulnerable Customer Detection Agent emitted an unmasked customer DOB inside a branch-advisor preview window for 8 seconds before the secondary PII filter (POL-BANK-105) scrubbed it. Auto-redacted on retry. Privacy team notified.",
    timeline: [
      { ts: "11 Mar 2026 09:14 AEDT", actor: "system",     event: "Output PII filter (POL-BANK-105) triggered on branch advisor preview." },
      { ts: "11 Mar 2026 09:16 AEDT", actor: "Sasha Nair", event: "CRO took ownership; classified as a near-miss not a confirmed breach (8-second window, no downstream copy)." },
      { ts: "11 Mar 2026 11:02 AEDT", actor: "Priya Ravi", event: "Legal confirmed no notification trigger under Privacy Act AU; recorded for the Privacy Commissioner annual return." },
      { ts: "12 Mar 2026 16:30 AEDT", actor: "Wei Lin Soo", event: "DS Lead deployed a pre-filter tweak (regex tightened on day-of-week tokens)." },
    ],
  },
  {
    id: "INC-BANK-0043",
    title: "Cost spike — Contact Centre Copilot up 30% WoW",
    agentId: "AGT-BANK-010",
    agentName: "Contact Centre Copilot",
    policyId: "POL-BANK-106",
    severity: "med",
    status: "triage",
    days: 1,
    owner: "Marcus Tan",
    ownerRole: "CIO",
    reported: "13 Mar 2026 09:00 AEDT",
    category: "Financial",
    impact: "FinOps cap reached 110% of monthly envelope ahead of schedule; soft throttle engaged.",
    narrative: "Contact Centre Copilot crossed the 110% cost cap threshold defined in POL-BANK-106. Soft throttle engaged automatically. CIO investigating whether the spike correlates with the EOFY tax-time hardship enquiry surge or a misconfigured retrieval index.",
    timeline: [
      { ts: "13 Mar 2026 09:00 AEDT", actor: "system",    event: "POL-BANK-106 cost cap crossed (110%); soft throttle engaged." },
      { ts: "13 Mar 2026 09:42 AEDT", actor: "Marcus Tan", event: "CIO confirmed retrieval index size doubled overnight; rolled back to v37." },
      { ts: "13 Mar 2026 16:10 AEDT", actor: "system",    event: "Cost trajectory normalised; throttle to be lifted at 18:00 if stable." },
    ],
  },
  {
    id: "INC-BANK-0042",
    title: "Accuracy drift on Fraud Case Summarisation Agent",
    agentId: "AGT-BANK-005",
    agentName: "Fraud Case Summarisation Agent",
    policyId: "POL-BANK-108",
    severity: "low",
    status: "closed",
    days: 0,
    owner: "Wei Lin Soo",
    ownerRole: "DS Lead",
    reported: "08 Mar 2026 16:20 AEDT",
    closedAt: "10 Mar 2026 14:18 AEDT",
    category: "Quality",
    impact: "Answer quality drifted from 91% → 87.4% over 7 days; below 88% warning band.",
    narrative: "POL-BANK-108 drift threshold tripped after a Friday model upgrade. Wei Lin Soo rolled back the prompt template and re-ran 200 sampled cases — accuracy recovered to 90.6%. Closure signed off by CRO.",
    timeline: [
      { ts: "08 Mar 2026 16:20 AEDT", actor: "system",     event: "Drift warning raised under POL-BANK-108." },
      { ts: "09 Mar 2026 09:00 AEDT", actor: "Wei Lin Soo", event: "DS Lead rolled prompt template back from v1.7 → v1.6.2." },
      { ts: "10 Mar 2026 14:18 AEDT", actor: "Sasha Nair", event: "CRO signed off closure after 24h of stable accuracy ≥ 90%." },
    ],
  },
  {
    id: "INC-BANK-0041",
    title: "Vendor outage — Google Vertex AI (15 min)",
    agentId: null,
    agentName: null,
    policyId: null,
    severity: "med",
    status: "closed",
    days: 0,
    owner: "Marcus Tan",
    ownerRole: "CIO",
    reported: "05 Mar 2026 11:08 AEDT",
    closedAt: "05 Mar 2026 13:00 AEDT",
    category: "Vendor",
    impact: "15-minute fall-back to OpenAI Enterprise; no customer-facing impact.",
    narrative: "Google Vertex AI ap-southeast-2 had a 15-minute control-plane outage. ALGOS failover routed traffic to OpenAI Enterprise. No customer-facing impact; vendor RCA filed.",
    timeline: [
      { ts: "05 Mar 2026 11:08 AEDT", actor: "system",    event: "Vertex AI heartbeat failed." },
      { ts: "05 Mar 2026 11:09 AEDT", actor: "system",    event: "Auto-failover to OpenAI Enterprise; user-facing latency +120ms." },
      { ts: "05 Mar 2026 11:23 AEDT", actor: "Marcus Tan", event: "Vendor confirmed restoration; failback initiated." },
    ],
  },
  {
    id: "INC-BANK-0040",
    title: "Vendor allow-list violation — Payments Anomaly Detection",
    agentId: "AGT-BANK-011",
    agentName: "Payments Anomaly Detection Agent",
    policyId: "POL-BANK-103",
    severity: "high",
    status: "triage",
    days: 2,
    owner: "Khalid Hassan",
    ownerRole: "IT Sec",
    reported: "12 Mar 2026 11:30 AEDT",
    category: "Vendor",
    impact: "Outbound call to perplexity.ai blocked at egress proxy; investigation underway.",
    narrative: "Payments Anomaly Detection Agent attempted an outbound call to perplexity.ai — not on the LLM vendor allow-list. Blocked at the egress proxy. Likely a stale RAG dependency from the pilot build; root cause pending.",
    timeline: [
      { ts: "12 Mar 2026 11:30 AEDT", actor: "system",        event: "POL-BANK-103 violation; outbound to perplexity.ai blocked." },
      { ts: "12 Mar 2026 11:34 AEDT", actor: "Khalid Hassan", event: "IT Sec took ownership; suspect stale pilot dependency in feature store." },
      { ts: "13 Mar 2026 14:50 AEDT", actor: "Marcus Tan",     event: "CIO removed legacy crawler config; awaiting redeploy validation." },
    ],
  },
  {
    id: "INC-BANK-0039",
    title: "Hallucination rate ↑ on Fraud Case Summariser (3% → 5.4%)",
    agentId: "AGT-BANK-005",
    agentName: "Fraud Case Summarisation Agent",
    policyId: "POL-BANK-108",
    severity: "med",
    status: "open",
    days: 4,
    owner: "Wei Lin Soo",
    ownerRole: "DS Lead",
    reported: "10 Mar 2026 14:05 AEDT",
    category: "Quality",
    impact: "Hallucination rate breached the 5% policy threshold under POL-BANK-108.",
    narrative: "Hallucination rate on the Fraud Case Summariser climbed from 3.0% → 5.4% week-over-week. Likely a stale grounding corpus. Investigation focused on the SAR-template retrieval bucket.",
    timeline: [
      { ts: "10 Mar 2026 14:05 AEDT", actor: "system",       event: "POL-BANK-108 hallucination threshold breached." },
      { ts: "11 Mar 2026 10:20 AEDT", actor: "Wei Lin Soo",  event: "DS Lead identified stale SAR-template corpus from Nov 2025." },
    ],
  },
  {
    id: "INC-BANK-0038",
    title: "Audit chain verify — Q1 pass",
    agentId: null,
    agentName: null,
    policyId: null,
    severity: "low",
    status: "closed",
    days: 0,
    owner: "system",
    ownerRole: "System",
    reported: "01 Mar 2026 06:00 AEDT",
    closedAt: "01 Mar 2026 06:01 AEDT",
    category: "Audit",
    impact: "Tamper-evident chain verified end-to-end across all 900k+ entries.",
    narrative: "Quarterly audit chain verification pass. Merkle root signed and filed against the Q1 2026 APRA CPS 234 evidence pack.",
    timeline: [
      { ts: "01 Mar 2026 06:00 AEDT", actor: "system", event: "Audit chain verification started." },
      { ts: "01 Mar 2026 06:01 AEDT", actor: "system", event: "Merkle root 1c44…9fa2 signed; all entries intact." },
    ],
  },
];

// ----- Human-in-the-Loop queue (S13) -----
// Decisions that ALGOS has paused for a human to review. Each item has an
// SLA countdown, a policy / agent cross-link, a suggested action, and a
// final decision verb (`approve` / `reject` / `escalate`).
export const DEMO_HIL_QUEUE = [
  {
    id: "HIL-BANK-0091",
    raisedAt: "14 Mar 2026 09:42 AEDT",
    agentId: "AGT-BANK-001",
    agentName: "AML Alert Triage Agent",
    department: "AML / CTF",
    kind: "Override recommendation",
    severity: "high",
    riskBand: "high",
    policyId: "POL-BANK-107",
    summary: "AML case AML-2026-3148: agent recommends closing as a false positive; PEP secondary indicator surfaced post-screening.",
    suggestedAction: "Manually review and escalate to AML Officer if confirmed PEP.",
    waitingOn: "S. Nair (CRO)",
    slaHoursRemaining: 4,
    slaTotalHours: 24,
    status: "pending",
  },
  {
    id: "HIL-BANK-0090",
    raisedAt: "14 Mar 2026 08:55 AEDT",
    agentId: "AGT-BANK-002",
    agentName: "Credit Risk Assessment Assistant",
    department: "Credit Risk",
    kind: "Approval (high-risk recommendation)",
    severity: "high",
    riskBand: "high",
    policyId: "POL-BANK-104",
    summary: "Loan application LN-882041: agent recommends approval at LVR 87%, debt-to-income 6.8x — exceeds soft policy thresholds.",
    suggestedAction: "Confirm exception rationale or reject with feedback to applicant.",
    waitingOn: "A. Wright (BU Head — Lending)",
    slaHoursRemaining: 18,
    slaTotalHours: 48,
    status: "pending",
  },
  {
    id: "HIL-BANK-0089",
    raisedAt: "14 Mar 2026 08:31 AEDT",
    agentId: "AGT-BANK-007",
    agentName: "Vulnerable Customer Detection Agent",
    department: "Customer Outcomes",
    kind: "Halt confirmation",
    severity: "high",
    riskBand: "high",
    policyId: "POL-BANK-105",
    summary: "Customer 0742-118 flagged as potentially vulnerable; agent paused all downstream actions pending human reaffirmation.",
    suggestedAction: "Confirm vulnerable-customer flag and route to specialist hardship team.",
    waitingOn: "P. Ravi (Legal & Compliance)",
    slaHoursRemaining: 2,
    slaTotalHours: 12,
    status: "pending",
  },
  {
    id: "HIL-BANK-0088",
    raisedAt: "13 Mar 2026 17:14 AEDT",
    agentId: "AGT-BANK-005",
    agentName: "Fraud Case Summarisation Agent",
    department: "Fraud Operations",
    kind: "Escalation",
    severity: "med",
    riskBand: "high",
    policyId: "POL-BANK-108",
    summary: "Fraud case FR-26-771: hallucination confidence flag triggered on the narrative section — second draft contradicts the first.",
    suggestedAction: "Discard the draft and re-run with a constrained-grounding prompt template.",
    waitingOn: "W. Soo (DS Lead)",
    slaHoursRemaining: 22,
    slaTotalHours: 48,
    status: "pending",
  },
  {
    id: "HIL-BANK-0087",
    raisedAt: "13 Mar 2026 15:42 AEDT",
    agentId: "AGT-BANK-004",
    agentName: "Mortgage Document Extraction Agent",
    department: "Mortgage Operations",
    kind: "Low-confidence extraction",
    severity: "med",
    riskBand: "med",
    policyId: "POL-BANK-104",
    summary: "12 of 38 extracted fields on the property valuation pack scored below 0.7 confidence (income table + rates notice).",
    suggestedAction: "Manually re-key the flagged fields or send back to the broker.",
    waitingOn: "A. Wright (BU Head — Lending)",
    slaHoursRemaining: 30,
    slaTotalHours: 48,
    status: "pending",
  },
  {
    id: "HIL-BANK-0086",
    raisedAt: "13 Mar 2026 13:08 AEDT",
    agentId: "AGT-BANK-011",
    agentName: "Payments Anomaly Detection Agent",
    department: "Payments",
    kind: "Override recommendation",
    severity: "high",
    riskBand: "high",
    policyId: "POL-BANK-103",
    summary: "Cross-border payment XB-3398 flagged at 96% anomaly score; agent recommends release based on 90-day pattern match.",
    suggestedAction: "Hold for sanctions screening or release with documented rationale.",
    waitingOn: "K. Hassan (IT Sec)",
    slaHoursRemaining: 14,
    slaTotalHours: 24,
    status: "pending",
  },
  {
    id: "HIL-BANK-0085",
    raisedAt: "12 Mar 2026 16:55 AEDT",
    agentId: "AGT-BANK-008",
    agentName: "Complaints Insights Agent",
    department: "Customer Outcomes",
    kind: "Theme reclassification",
    severity: "low",
    riskBand: "med",
    policyId: "POL-BANK-102",
    summary: "Agent proposes reclassifying 14 cases from 'Service' → 'Conduct Risk' based on a new theme dictionary.",
    suggestedAction: "Confirm or amend the proposed reclassification before downstream reporting.",
    waitingOn: "P. Ravi (Legal & Compliance)",
    slaHoursRemaining: 36,
    slaTotalHours: 72,
    status: "pending",
  },
  {
    id: "HIL-BANK-0084",
    raisedAt: "12 Mar 2026 11:18 AEDT",
    agentId: "AGT-BANK-013",
    agentName: "Treasury Narrative Drafter",
    department: "Treasury",
    kind: "Variance review",
    severity: "low",
    riskBand: "low",
    policyId: "POL-BANK-108",
    summary: "Daily liquidity narrative variance vs. prior-day baseline: -A$42M HQLA, +A$18M FX swap leg — agent flagged unusual pattern.",
    suggestedAction: "Validate against the Treasury blotter and approve narrative for distribution.",
    waitingOn: "J. Patel (CAIO)",
    slaHoursRemaining: 6,
    slaTotalHours: 8,
    status: "pending",
  },
];

// ----- Model drift / quality signals (S17) -----
// 12 weekly samples per agent for the Performance & Drift page.
// Newest sample is the last index. `status` is the rollup of all three
// dimensions vs. the policy threshold in POL-BANK-108.
const _series = (start, drift, jitter) =>
  Array.from({ length: 12 }, (_, i) => Math.max(0, +(start + i * drift + ((i * 7) % 5 - 2) * jitter).toFixed(2)));

export const DEMO_DRIFT_SIGNALS = [
  { agentId: "AGT-BANK-001", agentName: "AML Alert Triage Agent",            answerQuality: _series(94, -0.55, 0.20), hallucinationRate: _series(0.5,  0.18, 0.05), p95LatencyMs: _series(820,  14, 4), status: "warning", driftScore: 7.4, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-002", agentName: "Credit Risk Assessment Assistant",  answerQuality: _series(92, -0.30, 0.15), hallucinationRate: _series(0.8,  0.12, 0.04), p95LatencyMs: _series(940,  10, 3), status: "ok",      driftScore: 3.1, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-003", agentName: "KYC Review Copilot",                answerQuality: _series(96, -0.12, 0.08), hallucinationRate: _series(0.3,  0.05, 0.02), p95LatencyMs: _series(680,   6, 2), status: "ok",      driftScore: 1.4, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-004", agentName: "Mortgage Document Extraction Agent",answerQuality: _series(91, -0.25, 0.12), hallucinationRate: _series(1.2,  0.08, 0.03), p95LatencyMs: _series(1010, 18, 5), status: "ok",      driftScore: 4.2, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-005", agentName: "Fraud Case Summarisation Agent",    answerQuality: _series(91, -0.50, 0.30), hallucinationRate: _series(2.8,  0.22, 0.10), p95LatencyMs: _series(1180, 22, 6), status: "alert",   driftScore: 8.6, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-006", agentName: "AML Adverse Media Screening Agent", answerQuality: _series(93, -0.18, 0.10), hallucinationRate: _series(0.9,  0.07, 0.03), p95LatencyMs: _series(870,  9, 3),  status: "ok",      driftScore: 2.8, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-007", agentName: "Vulnerable Customer Detection Agent", answerQuality: _series(95, -0.20, 0.10), hallucinationRate: _series(0.4, 0.06, 0.02), p95LatencyMs: _series(710, 7, 2), status: "warning", driftScore: 6.1, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-008", agentName: "Complaints Insights Agent",         answerQuality: _series(89, -0.10, 0.10), hallucinationRate: _series(1.4,  0.05, 0.03), p95LatencyMs: _series(620,  4, 2), status: "ok",      driftScore: 2.0, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-009", agentName: "Branch Advisor Assist",             answerQuality: _series(90, -0.16, 0.10), hallucinationRate: _series(1.6,  0.06, 0.03), p95LatencyMs: _series(740,  5, 2), status: "ok",      driftScore: 2.4, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-010", agentName: "Contact Centre Copilot",            answerQuality: _series(88, -0.22, 0.14), hallucinationRate: _series(2.1,  0.10, 0.05), p95LatencyMs: _series(910, 16, 4), status: "warning", driftScore: 5.8, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-011", agentName: "Payments Anomaly Detection Agent",  answerQuality: _series(93, -0.28, 0.16), hallucinationRate: _series(1.1,  0.09, 0.04), p95LatencyMs: _series(950, 12, 4), status: "warning", driftScore: 5.3, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-012", agentName: "Collections Outreach Drafter",      answerQuality: _series(90, -0.12, 0.10), hallucinationRate: _series(1.8,  0.06, 0.03), p95LatencyMs: _series(700,  4, 2), status: "ok",      driftScore: 2.2, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-013", agentName: "Treasury Narrative Drafter",        answerQuality: _series(92, -0.08, 0.06), hallucinationRate: _series(0.7,  0.04, 0.02), p95LatencyMs: _series(620,  3, 1), status: "ok",      driftScore: 1.2, lastChecked: "14 Mar 2026 08:00 AEDT" },
  { agentId: "AGT-BANK-014", agentName: "Regulatory Reporting Assistant",    answerQuality: _series(94, -0.10, 0.06), hallucinationRate: _series(0.6,  0.04, 0.02), p95LatencyMs: _series(810,  4, 2), status: "ok",      driftScore: 1.6, lastChecked: "14 Mar 2026 08:00 AEDT" },
];

// ----- Deployment requests / approval workflow (active) -----
// Stages: 1 Submitted · 2 Under Review · 3 Governance Assessment · 4 Approval
// Required · 5 Approved · 6 Deployment Ready · 7 Deployed (see
// lib/deploymentWorkflow.js).
export const DEMO_REQUESTS = [
  {
    id: "DR-BANK-0421",
    agentName: "AML Alert Triage Agent",
    opportunityId: "OPP-BANK-2000",
    requestor: "S. Nair",
    department: "AML / CTF",
    businessUnit: "Financial Crime",
    risk: "high",
    stage: 3,
    status: "in-progress",
    slaDue: "02d 04h",
    submittedAt: "12 Mar 2026",
    updatedAt: "13 Mar 2026 09:14",
    decisions: [
      { ts: "12 Mar 2026 14:02", actor: "S. Nair",   decision: "approved", stage: 1 },
      { ts: "12 Mar 2026 16:31", actor: "K. Hassan", decision: "approved", stage: 2 },
    ],
  },
  {
    id: "DR-BANK-0420",
    agentName: "Credit Risk Assessment Assistant",
    opportunityId: "OPP-BANK-2001",
    requestor: "A. Wright",
    department: "Credit Risk",
    businessUnit: "Business Banking",
    risk: "high",
    stage: 4,
    status: "in-progress",
    slaDue: "00d 18h",
    submittedAt: "11 Mar 2026",
    updatedAt: "13 Mar 2026 17:20",
    decisions: [
      { ts: "11 Mar 2026 11:48", actor: "A. Wright", decision: "approved", stage: 1 },
      { ts: "11 Mar 2026 14:25", actor: "K. Hassan", decision: "approved", stage: 2 },
      { ts: "12 Mar 2026 10:09", actor: "P. Ravi",   decision: "approved", stage: 3 },
      { ts: "13 Mar 2026 09:14", actor: "K. Hassan", decision: "approved", stage: 4 },
    ],
  },
  {
    id: "DR-BANK-0419",
    agentName: "KYC Review Copilot",
    opportunityId: "OPP-BANK-2002",
    requestor: "M. Tan",
    department: "KYC / Onboarding",
    businessUnit: "Operations",
    risk: "med",
    stage: 6,
    status: "in-progress",
    slaDue: "—",
    submittedAt: "08 Mar 2026",
    updatedAt: "11 Mar 2026 12:00",
    decisions: [
      { ts: "08 Mar 2026 09:01", actor: "M. Tan",    decision: "approved", stage: 1 },
      { ts: "08 Mar 2026 15:42", actor: "K. Hassan", decision: "approved", stage: 2 },
      { ts: "11 Mar 2026 12:00", actor: "J. Patel",  decision: "approved", stage: 4 },
    ],
  },
  {
    id: "DR-BANK-0418",
    agentName: "Mortgage Document Extraction Agent",
    opportunityId: "OPP-BANK-2003",
    requestor: "A. Wright",
    department: "Mortgage Operations",
    businessUnit: "Retail Banking",
    risk: "med",
    stage: 2,
    status: "in-progress",
    slaDue: "01d 12h",
    submittedAt: "13 Mar 2026",
    updatedAt: "13 Mar 2026 10:45",
    decisions: [
      { ts: "13 Mar 2026 10:45", actor: "A. Wright", decision: "approved", stage: 1 },
    ],
  },
  {
    id: "DR-BANK-0417",
    agentName: "Treasury Narrative Drafter",
    opportunityId: "OPP-BANK-2006",
    requestor: "J. Patel",
    department: "Treasury",
    businessUnit: "Treasury",
    risk: "low",
    stage: 7,
    status: "deployed",
    terminal: true,
    slaDue: "—",
    submittedAt: "02 Mar 2026",
    updatedAt: "07 Mar 2026 09:30",
    decisions: [
      { ts: "02 Mar 2026 09:00", actor: "J. Patel",     decision: "approved", stage: 1 },
      { ts: "03 Mar 2026 10:10", actor: "K. Hassan",    decision: "approved", stage: 2 },
      { ts: "04 Mar 2026 11:11", actor: "J. Patel",     decision: "approved", stage: 4 },
      { ts: "06 Mar 2026 14:22", actor: "engineering",  decision: "approved", stage: 5 },
      { ts: "07 Mar 2026 09:30", actor: "operations",   decision: "approved", stage: 6 },
    ],
  },
  {
    id: "DR-BANK-0416",
    agentName: "Fraud Case Summarisation Agent",
    opportunityId: "OPP-BANK-2005",
    requestor: "K. Hassan",
    department: "Fraud Operations",
    businessUnit: "Financial Crime",
    risk: "high",
    stage: 1,
    status: "in-progress",
    slaDue: "03d",
    submittedAt: "14 Mar 2026",
    updatedAt: "14 Mar 2026 09:00",
    decisions: [
      { ts: "14 Mar 2026 09:00", actor: "K. Hassan", decision: "approved", stage: 1 },
    ],
  },
];

// ----- Compliance frameworks -----
// Each framework carries domains, controls (with status + linked policy +
// evidence count) and known gaps so S15 can render coverage rings, the
// control matrix, and an evidence drawer without any further fan-out.
export const DEMO_FRAMEWORKS = [
  {
    id: "eu-ai-act", name: "EU AI Act", jurisdiction: "European Union",
    coverage: 86, mappedControls: "43 / 50", controlsTotal: 50, controlsMet: 43, controlsPartial: 5, controlsGap: 2,
    coverageHistory: [70, 71, 73, 74, 76, 78, 79, 80, 82, 83, 85, 86],
    lastEvidenceTs: "08 Mar 2026", lastAuditor: "P. Ravi",
    summary: "Risk classification, technical documentation, transparency, human oversight, post-market monitoring for high-risk AI systems.",
    domains: [
      { id: "risk", name: "Risk management", controlsMet: 11, controlsTotal: 12 },
      { id: "data", name: "Data governance",  controlsMet: 9,  controlsTotal: 11 },
      { id: "transparency", name: "Transparency & user info", controlsMet: 8, controlsTotal: 9 },
      { id: "oversight", name: "Human oversight", controlsMet: 7, controlsTotal: 8 },
      { id: "monitoring", name: "Post-market monitoring", controlsMet: 8, controlsTotal: 10 },
    ],
    controls: [
      { id: "EUAI-A09-01", domain: "risk",         name: "Article 9 — Risk management system",          status: "implemented", policyIds: ["POL-BANK-104", "POL-BANK-108"], evidenceCount: 12 },
      { id: "EUAI-A10-01", domain: "data",         name: "Article 10 — Training data quality controls", status: "implemented", policyIds: ["POL-BANK-102", "POL-BANK-107"], evidenceCount: 9  },
      { id: "EUAI-A11-01", domain: "transparency", name: "Article 11 — Technical documentation",        status: "implemented", policyIds: ["POL-BANK-105"],            evidenceCount: 6  },
      { id: "EUAI-A13-01", domain: "transparency", name: "Article 13 — User information & instructions",status: "partial",     policyIds: ["POL-BANK-105"],            evidenceCount: 3  },
      { id: "EUAI-A14-01", domain: "oversight",    name: "Article 14 — Human oversight provisions",     status: "implemented", policyIds: ["POL-BANK-104"],            evidenceCount: 8  },
      { id: "EUAI-A15-01", domain: "monitoring",   name: "Article 15 — Accuracy, robustness, security", status: "partial",     policyIds: ["POL-BANK-108"],            evidenceCount: 4  },
      { id: "EUAI-A61-01", domain: "monitoring",   name: "Article 61 — Post-market monitoring plan",    status: "gap",         policyIds: [],                     evidenceCount: 0  },
    ],
    gaps: [
      { id: "EUAI-A61-01", title: "Post-market monitoring plan not yet signed", owner: "P. Ravi", dueBy: "30 Apr 2026" },
      { id: "EUAI-A60-01", title: "Article 60 — incident reporting playbook draft", owner: "K. Hassan", dueBy: "15 May 2026" },
    ],
  },
  {
    id: "nist-ai-rmf", name: "NIST AI RMF", jurisdiction: "United States",
    coverage: 91, mappedControls: "64 / 70", controlsTotal: 70, controlsMet: 64, controlsPartial: 4, controlsGap: 2,
    coverageHistory: [82, 83, 85, 86, 87, 87, 88, 89, 89, 90, 91, 91],
    lastEvidenceTs: "05 Mar 2026", lastAuditor: "W. Soo",
    summary: "GOVERN · MAP · MEASURE · MANAGE — voluntary risk management framework adopted as the NIST baseline for enterprise AI assurance.",
    domains: [
      { id: "govern",  name: "GOVERN",  controlsMet: 18, controlsTotal: 19 },
      { id: "map",     name: "MAP",     controlsMet: 15, controlsTotal: 17 },
      { id: "measure", name: "MEASURE", controlsMet: 16, controlsTotal: 17 },
      { id: "manage",  name: "MANAGE",  controlsMet: 15, controlsTotal: 17 },
    ],
    controls: [
      { id: "NIST-GV-1.1", domain: "govern",  name: "GOVERN 1.1 — Policies & accountability structure", status: "implemented", policyIds: ["POL-BANK-104", "POL-BANK-103"], evidenceCount: 11 },
      { id: "NIST-MP-2.3", domain: "map",     name: "MAP 2.3 — Categorisation of AI systems by impact", status: "implemented", policyIds: ["POL-BANK-107"],            evidenceCount: 7  },
      { id: "NIST-MS-2.6", domain: "measure", name: "MEASURE 2.6 — Trustworthiness characteristics",    status: "implemented", policyIds: ["POL-BANK-108"],            evidenceCount: 9  },
      { id: "NIST-MG-2.4", domain: "manage",  name: "MANAGE 2.4 — Risk response actions tracked",       status: "partial",     policyIds: ["POL-BANK-108"],            evidenceCount: 3  },
      { id: "NIST-MG-4.1", domain: "manage",  name: "MANAGE 4.1 — Post-deployment monitoring",          status: "gap",         policyIds: [],                     evidenceCount: 0  },
    ],
    gaps: [
      { id: "NIST-MG-4.1", title: "Post-deployment monitoring evidence pack missing", owner: "W. Soo", dueBy: "10 May 2026" },
    ],
  },
  {
    id: "iso-42001", name: "ISO 42001", jurisdiction: "International",
    coverage: 78, mappedControls: "28 / 36", controlsTotal: 36, controlsMet: 28, controlsPartial: 5, controlsGap: 3,
    coverageHistory: [62, 64, 66, 68, 70, 71, 72, 74, 75, 76, 77, 78],
    lastEvidenceTs: "20 Feb 2026", lastAuditor: "P. Ravi",
    summary: "AI Management System (AIMS) — ISO standard for governing the AI lifecycle. Recently certified for the Acme Bank AI estate.",
    domains: [
      { id: "context",   name: "Context & leadership", controlsMet: 6, controlsTotal: 7 },
      { id: "planning",  name: "Planning & objectives", controlsMet: 5, controlsTotal: 6 },
      { id: "support",   name: "Support & resources",   controlsMet: 4, controlsTotal: 6 },
      { id: "operation", name: "Operation",             controlsMet: 7, controlsTotal: 9 },
      { id: "improve",   name: "Improvement",           controlsMet: 6, controlsTotal: 8 },
    ],
    controls: [
      { id: "ISO-5.1", domain: "context",   name: "Clause 5.1 — Leadership commitment to AIMS",         status: "implemented", policyIds: ["POL-BANK-104"],            evidenceCount: 5 },
      { id: "ISO-6.2", domain: "planning",  name: "Clause 6.2 — AI objectives & planning to meet them", status: "implemented", policyIds: ["POL-BANK-103"],            evidenceCount: 4 },
      { id: "ISO-8.4", domain: "operation", name: "Clause 8.4 — AI third-party supplier management",    status: "partial",     policyIds: ["POL-BANK-103"],            evidenceCount: 3 },
      { id: "ISO-9.1", domain: "improve",   name: "Clause 9.1 — Monitoring, measurement, analysis",     status: "implemented", policyIds: ["POL-BANK-108"],            evidenceCount: 7 },
      { id: "ISO-A.10",domain: "operation", name: "Annex A.10 — Quality of training & validation data", status: "gap",         policyIds: [],                     evidenceCount: 0 },
    ],
    gaps: [
      { id: "ISO-A.10", title: "Training-data quality evidence not yet collected", owner: "W. Soo", dueBy: "30 Jun 2026" },
    ],
  },
  {
    id: "apra-cps-234", name: "APRA CPS 234", jurisdiction: "Australia · APRA",
    coverage: 94, mappedControls: "32 / 34", controlsTotal: 34, controlsMet: 32, controlsPartial: 2, controlsGap: 0,
    coverageHistory: [88, 89, 89, 90, 91, 91, 92, 92, 93, 93, 94, 94],
    lastEvidenceTs: "01 Apr 2026", lastAuditor: "S. Nair",
    summary: "APRA prudential information-security guidance. Mandatory for regulated AFIs — ALGOS provides automated CPS 234 evidence assembly.",
    domains: [
      { id: "info",      name: "Information security capability", controlsMet: 9,  controlsTotal: 9 },
      { id: "controls",  name: "Information security controls",    controlsMet: 11, controlsTotal: 12 },
      { id: "incidents", name: "Incident management",              controlsMet: 7,  controlsTotal: 8 },
      { id: "testing",   name: "Testing & assurance",              controlsMet: 5,  controlsTotal: 5 },
    ],
    controls: [
      { id: "APRA-15", domain: "info",      name: "Clause 15 — Roles and responsibilities documented", status: "implemented", policyIds: ["POL-BANK-104"],            evidenceCount: 6 },
      { id: "APRA-23", domain: "controls",  name: "Clause 23 — Vendor management of cloud & AI",       status: "implemented", policyIds: ["POL-BANK-103", "POL-BANK-101"], evidenceCount: 8 },
      { id: "APRA-32", domain: "controls",  name: "Clause 32 — Information classification",            status: "implemented", policyIds: ["POL-BANK-107"],            evidenceCount: 7 },
      { id: "APRA-40", domain: "incidents", name: "Clause 40 — Incident notification within 72h",      status: "implemented", policyIds: [],                     evidenceCount: 4 },
      { id: "APRA-46", domain: "testing",   name: "Clause 46 — Systematic testing programme",          status: "partial",     policyIds: ["POL-BANK-108"],            evidenceCount: 3 },
    ],
    gaps: [],
  },
  {
    id: "privacy-au", name: "Privacy Act (AU)", jurisdiction: "Australia · OAIC",
    coverage: 89, mappedControls: "24 / 27", controlsTotal: 27, controlsMet: 24, controlsPartial: 2, controlsGap: 1,
    coverageHistory: [80, 81, 82, 83, 84, 85, 86, 87, 87, 88, 89, 89],
    lastEvidenceTs: "20 Feb 2026", lastAuditor: "P. Ravi",
    summary: "Australian Privacy Principles (APPs 1–13). ALGOS enforces residency, masking and consent across every AI workflow that touches PII.",
    domains: [
      { id: "open",      name: "Open & transparent management",  controlsMet: 4, controlsTotal: 4 },
      { id: "collection",name: "Collection & use",               controlsMet: 6, controlsTotal: 7 },
      { id: "quality",   name: "Data quality & security",        controlsMet: 8, controlsTotal: 9 },
      { id: "access",    name: "Access, correction & complaints", controlsMet: 6, controlsTotal: 7 },
    ],
    controls: [
      { id: "APP-1.2", domain: "open",       name: "APP 1.2 — Privacy policy publicly available",  status: "implemented", policyIds: ["POL-BANK-102"],            evidenceCount: 2 },
      { id: "APP-3.1", domain: "collection", name: "APP 3.1 — Collection limited to necessary",    status: "implemented", policyIds: ["POL-BANK-107"],            evidenceCount: 5 },
      { id: "APP-6.1", domain: "collection", name: "APP 6.1 — Use & disclosure of personal info",  status: "partial",     policyIds: ["POL-BANK-102"],            evidenceCount: 3 },
      { id: "APP-8.1", domain: "quality",    name: "APP 8.1 — Cross-border disclosure controls",   status: "implemented", policyIds: ["POL-BANK-101"],            evidenceCount: 6 },
      { id: "APP-11.1",domain: "quality",    name: "APP 11.1 — Security of personal information",  status: "implemented", policyIds: ["POL-BANK-102", "POL-BANK-105"], evidenceCount: 8 },
      { id: "APP-12.1",domain: "access",     name: "APP 12.1 — Access to personal information",    status: "gap",         policyIds: [],                     evidenceCount: 0 },
    ],
    gaps: [
      { id: "APP-12.1", title: "AI-mediated subject access request workflow incomplete", owner: "P. Ravi", dueBy: "30 Apr 2026" },
    ],
  },
  {
    id: "asic-rg-271", name: "ASIC RG 271", jurisdiction: "Australia · ASIC",
    coverage: 100, mappedControls: "12 / 12", controlsTotal: 12, controlsMet: 12, controlsPartial: 0, controlsGap: 0,
    coverageHistory: [92, 93, 94, 96, 97, 98, 99, 100, 100, 100, 100, 100],
    lastEvidenceTs: "15 Feb 2026", lastAuditor: "P. Ravi",
    summary: "Internal dispute resolution guidance. All customer-facing AI agents inherit the ASIC RG 271 IDR controls automatically.",
    domains: [
      { id: "design",   name: "IDR design",       controlsMet: 4, controlsTotal: 4 },
      { id: "operate",  name: "IDR operation",    controlsMet: 5, controlsTotal: 5 },
      { id: "reporting",name: "IDR reporting",    controlsMet: 3, controlsTotal: 3 },
    ],
    controls: [
      { id: "RG271-15", domain: "design",   name: "Para 15 — Acknowledgement within 24h",      status: "implemented", policyIds: ["POL-BANK-105"], evidenceCount: 4 },
      { id: "RG271-46", domain: "operate",  name: "Para 46 — IDR response within 30 days",     status: "implemented", policyIds: ["POL-BANK-105"], evidenceCount: 3 },
      { id: "RG271-91", domain: "reporting",name: "Para 91 — IDR data reporting to ASIC",      status: "implemented", policyIds: [],          evidenceCount: 2 },
    ],
    gaps: [],
  },
  {
    id: "au-ai-ethics", name: "AU AI Ethics", jurisdiction: "Australia",
    coverage: 95, mappedControls: "20 / 21", controlsTotal: 21, controlsMet: 20, controlsPartial: 1, controlsGap: 0,
    coverageHistory: [87, 88, 89, 90, 91, 92, 93, 94, 94, 95, 95, 95],
    lastEvidenceTs: "28 Feb 2026", lastAuditor: "J. Patel",
    summary: "Australia's 8 AI Ethics Principles — human-centred values, fairness, privacy, reliability, transparency, contestability, accountability.",
    domains: [
      { id: "values",       name: "Human-centred values",  controlsMet: 4, controlsTotal: 4 },
      { id: "fairness",     name: "Fairness",              controlsMet: 3, controlsTotal: 3 },
      { id: "transparency", name: "Transparency",          controlsMet: 4, controlsTotal: 4 },
      { id: "accountability", name: "Accountability",      controlsMet: 5, controlsTotal: 5 },
      { id: "reliability",  name: "Reliability & safety",  controlsMet: 4, controlsTotal: 5 },
    ],
    controls: [
      { id: "AEP-1", domain: "values",        name: "Principle 1 — Human, social & environmental wellbeing", status: "implemented", policyIds: ["POL-BANK-104"], evidenceCount: 3 },
      { id: "AEP-3", domain: "fairness",      name: "Principle 3 — Fairness, no unfair discrimination",      status: "implemented", policyIds: ["POL-BANK-107"], evidenceCount: 5 },
      { id: "AEP-5", domain: "transparency",  name: "Principle 5 — Transparency & explainability",           status: "implemented", policyIds: ["POL-BANK-105"], evidenceCount: 4 },
      { id: "AEP-8", domain: "accountability",name: "Principle 8 — Accountability",                          status: "implemented", policyIds: ["POL-BANK-104"], evidenceCount: 4 },
      { id: "AEP-4", domain: "reliability",   name: "Principle 4 — Reliability & safety",                    status: "partial",     policyIds: ["POL-BANK-108"], evidenceCount: 2 },
    ],
    gaps: [],
  },
];

// ----- Audit log entries -----
// Tamper-evident hash-chained ledger. Each entry references the previous
// entry's `hash` and produces its own deterministic mock hash (12 hex chars,
// truncated for UI). The `entityType` drives cross-link drill-down in S16.
// Severity is one of: low · med · high · critical (system-only).
const AUDIT_ENTRIES_RAW = [
  // Most recent first when read top-to-bottom in the UI.
  // Each entry carries a `narrative` field (human-readable sentence) rendered
  // in the UI alongside the verb-coded `action` (for filters & API parity).
  { ts: "14 Mar 2026 09:12 AEDT", actor: "Khalid Hassan",  action: "workflow.approve.stage2",  narrative: "Khalid Hassan approved Stage 2 IT Security review on DR-BANK-0421 (AML Alert Triage Agent).",                                              entityType: "request",     entityRef: "DR-BANK-0421",         severity: "med",  ip: "10.21.4.18" },
  { ts: "14 Mar 2026 09:11 AEDT", actor: "system",          action: "policy.evaluate",          narrative: "System evaluated POL-BANK-101 (AU Data Residency) against the AML Alert Triage Agent — pass.",                                                     entityType: "policy",      entityRef: "POL-BANK-101",         severity: "low",  ip: "internal" },
  { ts: "14 Mar 2026 09:10 AEDT", actor: "Sasha Nair",      action: "request.submit",           narrative: "Sasha Nair submitted deployment request DR-BANK-0421 for the AML Alert Triage Agent.",                                                            entityType: "request",     entityRef: "DR-BANK-0421",         severity: "med",  ip: "10.21.4.66" },
  { ts: "14 Mar 2026 08:58 AEDT", actor: "Sasha Nair",      action: "policy.publish",           narrative: "Sasha Nair published POL-BANK-105 (Output PII Filter) v2 — added cross-customer leakage clause.",                                                  entityType: "policy",      entityRef: "POL-BANK-105",         severity: "high", ip: "10.21.4.12" },
  { ts: "14 Mar 2026 08:52 AEDT", actor: "system",          action: "audit.chain.verify",       narrative: "System verified the audit chain end-to-end — Merkle root 3a9b…7e21, all entries intact.",                                                          entityType: "system",      entityRef: "merkle:3a9b…7e21",     severity: "low",  ip: "internal" },
  { ts: "14 Mar 2026 08:33 AEDT", actor: "Marcus Tan",      action: "vendor.connect",           narrative: "Marcus Tan re-attested the OpenAI Enterprise vendor connection — MSA and AU residency confirmed.",                                                 entityType: "vendor",      entityRef: "openai",               severity: "med",  ip: "10.21.4.41" },
  { ts: "14 Mar 2026 08:01 AEDT", actor: "system",          action: "report.generated",         narrative: "System generated RPT-BANK-APRA-Q1-2026 — APRA CPS 234 quarterly evidence pack, ALGOS-signed.",                                                     entityType: "report",      entityRef: "RPT-BANK-APRA-Q1-2026", severity: "low",  ip: "internal" },
  { ts: "13 Mar 2026 17:40 AEDT", actor: "Wei Lin Soo",     action: "agent.update",             narrative: "Wei Lin Soo updated AGT-BANK-005 (Fraud Case Summarisation Agent) to v1.6.2 — prompt safety filter v3.",                                           entityType: "agent",       entityRef: "AGT-BANK-005",         severity: "med",  ip: "10.21.4.91" },
  { ts: "13 Mar 2026 17:18 AEDT", actor: "Khalid Hassan",   action: "workflow.approve.stage4",  narrative: "Khalid Hassan recorded Stage 4 IT Security approval on DR-BANK-0420 (Credit Risk Assessment Assistant).",                                          entityType: "request",     entityRef: "DR-BANK-0420",         severity: "high", ip: "10.21.4.18" },
  { ts: "13 Mar 2026 16:55 AEDT", actor: "system",          action: "policy.violation.detected", narrative: "System detected POL-BANK-103 violation — Payments Anomaly Detection Agent attempted call to a non-allow-listed LLM vendor; blocked.",            entityType: "policy",      entityRef: "POL-BANK-103",         severity: "high", ip: "internal" },
  { ts: "13 Mar 2026 16:30 AEDT", actor: "system",          action: "incident.opened",          narrative: "System opened INC-BANK-0040 — vendor allow-list violation traced to AGT-BANK-011, assigned to Khalid Hassan.",                                     entityType: "incident",    entityRef: "INC-BANK-0040",        severity: "high", ip: "internal" },
  { ts: "13 Mar 2026 14:10 AEDT", actor: "Jordan Patel",    action: "framework.evidence.upload",narrative: "Jordan Patel uploaded EU AI Act Article 9 risk-management evidence pack for the Q1 2026 audit cycle.",                                            entityType: "framework",   entityRef: "EU AI Act",            severity: "low",  ip: "10.21.4.07" },
  { ts: "13 Mar 2026 13:42 AEDT", actor: "Aroha Wright",    action: "opportunity.submit",       narrative: "Aroha Wright submitted opportunity OPP-BANK-2000 — AML alert triage with rationale (estimated A$3.6M annual value).",                              entityType: "opportunity", entityRef: "OPP-BANK-2000",        severity: "low",  ip: "10.21.4.66" },
  { ts: "13 Mar 2026 13:14 AEDT", actor: "Priya Ravi",      action: "workflow.approve.stage3",  narrative: "Priya Ravi recorded Stage 3 Legal & Compliance assessment on DR-BANK-0421 (AML Alert Triage Agent).",                                              entityType: "request",     entityRef: "DR-BANK-0421",         severity: "med",  ip: "10.21.4.55" },
  { ts: "13 Mar 2026 12:01 AEDT", actor: "system",          action: "policy.evaluate",          narrative: "System swept POL-BANK-108 (Model Drift Threshold) across the governed estate — 1 warning surfaced.",                                              entityType: "policy",      entityRef: "POL-BANK-108",         severity: "low",  ip: "internal" },
  { ts: "13 Mar 2026 11:45 AEDT", actor: "Wei Lin Soo",     action: "agent.deploy",             narrative: "Wei Lin Soo promoted AGT-BANK-004 (Mortgage Document Extraction Agent) to production.",                                                            entityType: "agent",       entityRef: "AGT-BANK-004",         severity: "med",  ip: "10.21.4.91" },
  { ts: "13 Mar 2026 11:02 AEDT", actor: "system",          action: "drift.signal.detected",    narrative: "System detected drift on AGT-BANK-005 (Fraud Case Summarisation Agent) — answer quality 82.4% over 7 days.",                                       entityType: "agent",       entityRef: "AGT-BANK-005",         severity: "med",  ip: "internal" },
  { ts: "13 Mar 2026 10:45 AEDT", actor: "Aroha Wright",    action: "request.submit",           narrative: "Aroha Wright submitted deployment request DR-BANK-0418 for the Mortgage Document Extraction Agent.",                                               entityType: "request",     entityRef: "DR-BANK-0418",         severity: "med",  ip: "10.21.4.41" },
  { ts: "13 Mar 2026 09:14 AEDT", actor: "Khalid Hassan",   action: "workflow.approve.stage2",  narrative: "Khalid Hassan recorded Stage 2 IT Security approval on DR-BANK-0421 (AML Alert Triage Agent).",                                                   entityType: "request",     entityRef: "DR-BANK-0421",         severity: "med",  ip: "10.21.4.18" },
  { ts: "12 Mar 2026 17:45 AEDT", actor: "system",          action: "report.generated",         narrative: "System generated RPT-BANK-COST-W11 — weekly cost-by-vendor breakdown for the AI portfolio.",                                                       entityType: "report",      entityRef: "RPT-BANK-COST-W11",    severity: "low",  ip: "internal" },
  { ts: "12 Mar 2026 16:31 AEDT", actor: "Khalid Hassan",   action: "workflow.approve.stage2",  narrative: "Khalid Hassan recorded a second Stage 2 IT Security pass on DR-BANK-0421 after the residency clarification.",                                     entityType: "request",     entityRef: "DR-BANK-0421",         severity: "med",  ip: "10.21.4.18" },
  { ts: "12 Mar 2026 14:02 AEDT", actor: "Aroha Wright",    action: "opportunity.endorse",      narrative: "Aroha Wright endorsed OPP-BANK-2000 for governance assessment after BU Head sign-off.",                                                            entityType: "opportunity", entityRef: "OPP-BANK-2000",        severity: "low",  ip: "10.21.4.66" },
  { ts: "12 Mar 2026 13:18 AEDT", actor: "Priya Ravi",      action: "framework.audit.start",    narrative: "Priya Ravi opened the EU AI Act Q1 2026 audit cycle and assigned evidence owners.",                                                                entityType: "framework",   entityRef: "EU AI Act",            severity: "med",  ip: "10.21.4.30" },
  { ts: "12 Mar 2026 12:50 AEDT", actor: "Sasha Nair",      action: "policy.evaluate",          narrative: "Sasha Nair forced re-evaluation of POL-BANK-101 (AU Data Residency) — 0 active breaches across the governed estate.",                              entityType: "policy",      entityRef: "POL-BANK-101",         severity: "high", ip: "internal" },
  { ts: "12 Mar 2026 11:30 AEDT", actor: "system",          action: "policy.violation.detected",narrative: "System detected POL-BANK-103 violation — Payments Anomaly Detection Agent attempted outbound to perplexity.ai; blocked at the egress proxy.",     entityType: "policy",      entityRef: "POL-BANK-103",         severity: "high", ip: "internal" },
  { ts: "12 Mar 2026 10:09 AEDT", actor: "Priya Ravi",      action: "workflow.approve.stage3",  narrative: "Priya Ravi recorded Stage 3 Legal & Compliance approval on DR-BANK-0420 (Credit Risk Assessment Assistant).",                                      entityType: "request",     entityRef: "DR-BANK-0420",         severity: "high", ip: "10.21.4.55" },
  { ts: "11 Mar 2026 18:00 AEDT", actor: "system",          action: "session.timeout",          narrative: "System auto-signed-out idle SSO session sess-7c2a after 30 minutes of inactivity.",                                                                entityType: "session",     entityRef: "sess-7c2a",            severity: "low",  ip: "internal" },
  { ts: "11 Mar 2026 17:20 AEDT", actor: "Aroha Wright",    action: "request.submit",           narrative: "Aroha Wright submitted deployment request DR-BANK-0420 for the Credit Risk Assessment Assistant.",                                                 entityType: "request",     entityRef: "DR-BANK-0420",         severity: "high", ip: "10.21.4.12" },
  { ts: "11 Mar 2026 14:25 AEDT", actor: "Khalid Hassan",   action: "workflow.approve.stage2",  narrative: "Khalid Hassan recorded Stage 2 IT Security approval on DR-BANK-0420 (Credit Risk Assessment Assistant).",                                          entityType: "request",     entityRef: "DR-BANK-0420",         severity: "high", ip: "10.21.4.18" },
  { ts: "11 Mar 2026 13:12 AEDT", actor: "system",          action: "audit.chain.verify",       narrative: "System verified the audit chain — Merkle root 8d1f…b04e, all entries intact.",                                                                     entityType: "system",      entityRef: "merkle:8d1f…b04e",     severity: "low",  ip: "internal" },
  { ts: "11 Mar 2026 12:00 AEDT", actor: "Jordan Patel",    action: "workflow.approve.stage4",  narrative: "Jordan Patel recorded Stage 4 CAIO approval on DR-BANK-0419 (KYC Review Copilot) — moved to deployment ready.",                                    entityType: "request",     entityRef: "DR-BANK-0419",         severity: "low",  ip: "10.21.4.07" },
  { ts: "11 Mar 2026 11:48 AEDT", actor: "Sasha Nair",      action: "request.submit",           narrative: "Sasha Nair submitted deployment request DR-BANK-0420 for the Credit Risk Assessment Assistant.",                                                   entityType: "request",     entityRef: "DR-BANK-0420",         severity: "high", ip: "10.21.4.12" },
  { ts: "11 Mar 2026 09:14 AEDT", actor: "system",          action: "policy.evaluate",          narrative: "System ran the morning sweep of POL-BANK-101 (AU Data Residency) — 1 violation surfaced on AGT-BANK-007.",                                         entityType: "policy",      entityRef: "POL-BANK-101",         severity: "high", ip: "internal" },
  { ts: "10 Mar 2026 16:30 AEDT", actor: "Wei Lin Soo",     action: "agent.update",             narrative: "Wei Lin Soo updated AGT-BANK-002 (Credit Risk Assessment Assistant) to v1.4.2 — refreshed exception templates.",                                   entityType: "agent",       entityRef: "AGT-BANK-002",         severity: "med",  ip: "10.21.4.91" },
  { ts: "10 Mar 2026 14:05 AEDT", actor: "system",          action: "incident.opened",          narrative: "System opened INC-BANK-0039 — hallucination rate on the Fraud Case Summarisation Agent crossed the 5% threshold.",                                  entityType: "incident",    entityRef: "INC-BANK-0039",        severity: "med",  ip: "internal" },
  { ts: "10 Mar 2026 11:00 AEDT", actor: "Wei Lin Soo",     action: "framework.control.update", narrative: "Wei Lin Soo updated ISO 42001 Clause 9.1 evidence — added the latest drift monitoring pack.",                                                     entityType: "framework",   entityRef: "ISO 42001",            severity: "low",  ip: "10.21.4.91" },
  { ts: "10 Mar 2026 09:15 AEDT", actor: "Jordan Patel",    action: "policy.publish",           narrative: "Jordan Patel published POL-BANK-104 (Autonomy Cap — Recommend) v1 draft for CRO review.",                                                          entityType: "policy",      entityRef: "POL-BANK-104",         severity: "high", ip: "10.21.4.07" },
  { ts: "09 Mar 2026 18:22 AEDT", actor: "Marcus Tan",      action: "vendor.disconnect",        narrative: "Marcus Tan disconnected the Perplexity AI vendor connection — failed vendor risk reassessment.",                                                   entityType: "vendor",      entityRef: "perplexity",           severity: "med",  ip: "10.21.4.41" },
  { ts: "09 Mar 2026 15:50 AEDT", actor: "Priya Ravi",      action: "framework.evidence.upload",narrative: "Priya Ravi uploaded the Privacy Act AU APP 11.1 security-of-information evidence pack.",                                                            entityType: "framework",   entityRef: "Privacy Act (AU)",     severity: "low",  ip: "10.21.4.30" },
  { ts: "09 Mar 2026 14:00 AEDT", actor: "system",          action: "policy.evaluate",          narrative: "System evaluated POL-BANK-105 (Output PII Filter) across customer-facing agents — 0 leaks detected.",                                              entityType: "policy",      entityRef: "POL-BANK-105",         severity: "low",  ip: "internal" },
  { ts: "09 Mar 2026 11:30 AEDT", actor: "Wei Lin Soo",     action: "agent.update",             narrative: "Wei Lin Soo updated AGT-BANK-008 (Complaints Insights Agent) to v1.5.1 — added conduct-risk theme dictionary.",                                    entityType: "agent",       entityRef: "AGT-BANK-008",         severity: "med",  ip: "10.21.4.91" },
  { ts: "08 Mar 2026 16:20 AEDT", actor: "system",          action: "drift.signal.detected",    narrative: "System detected accuracy drift on AGT-BANK-005 (Fraud Case Summarisation Agent) — opened INC-BANK-0042.",                                          entityType: "agent",       entityRef: "AGT-BANK-005",         severity: "med",  ip: "internal" },
  { ts: "08 Mar 2026 15:42 AEDT", actor: "Khalid Hassan",   action: "workflow.approve.stage2",  narrative: "Khalid Hassan recorded Stage 2 IT Security approval on DR-BANK-0419 (KYC Review Copilot).",                                                       entityType: "request",     entityRef: "DR-BANK-0419",         severity: "low",  ip: "10.21.4.18" },
  { ts: "08 Mar 2026 11:22 AEDT", actor: "Priya Ravi",      action: "report.generated",         narrative: "Priya Ravi generated RPT-BANK-EUAIACT-Q1 — EU AI Act Article 9 evidence report.",                                                                   entityType: "report",      entityRef: "RPT-BANK-EUAIACT-Q1",  severity: "low",  ip: "10.21.4.30" },
  { ts: "08 Mar 2026 09:01 AEDT", actor: "Marcus Tan",      action: "request.submit",           narrative: "Marcus Tan submitted deployment request DR-BANK-0419 for the KYC Review Copilot.",                                                                  entityType: "request",     entityRef: "DR-BANK-0419",         severity: "low",  ip: "10.21.4.91" },
  { ts: "07 Mar 2026 14:30 AEDT", actor: "Jordan Patel",    action: "opportunity.endorse",      narrative: "Jordan Patel endorsed OPP-BANK-2003 — KYC document classification at intake.",                                                                      entityType: "opportunity", entityRef: "OPP-BANK-2003",        severity: "low",  ip: "10.21.4.07" },
  { ts: "07 Mar 2026 09:30 AEDT", actor: "operations",      action: "workflow.approve.stage6",  narrative: "Operations promoted DR-BANK-0417 (Treasury Narrative Drafter) to production — deployment complete.",                                                entityType: "request",     entityRef: "DR-BANK-0417",         severity: "low",  ip: "10.0.0.5" },
  { ts: "06 Mar 2026 14:22 AEDT", actor: "engineering",     action: "workflow.approve.stage5",  narrative: "Engineering signed off DR-BANK-0417 (Treasury Narrative Drafter) as deployment ready.",                                                            entityType: "request",     entityRef: "DR-BANK-0417",         severity: "low",  ip: "10.0.0.4" },
  { ts: "06 Mar 2026 10:48 AEDT", actor: "Sasha Nair",      action: "policy.violation.detected",narrative: "Sasha Nair flagged a POL-BANK-101 residency edge case — embedding service briefly routed outside ap-southeast-2; auto-blocked.",                  entityType: "policy",      entityRef: "POL-BANK-101",         severity: "high", ip: "internal" },
  { ts: "05 Mar 2026 17:00 AEDT", actor: "system",          action: "audit.chain.verify",       narrative: "System verified the audit chain — Merkle root 1c44…9fa2, all entries intact.",                                                                    entityType: "system",      entityRef: "merkle:1c44…9fa2",     severity: "low",  ip: "internal" },
];

// Generate deterministic 12-char "hash" strings so the chain visualisation
// in S16 looks credible without us bringing in a real SHA implementation.
function _mockHash(seed) {
  // FNV-1a style 32-bit hash, expanded to 12 hex chars (6 bytes) by mixing.
  let h = 0x811c9dc5 >>> 0;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  const a = h.toString(16).padStart(8, "0");
  // Mix again for a second word — keeps the visual entropy high.
  let g = h ^ 0xdeadbeef;
  for (let i = 0; i < seed.length; i++) {
    g ^= seed.charCodeAt((i * 7) % seed.length);
    g = Math.imul(g, 0x100193b3) >>> 0;
  }
  const b = g.toString(16).padStart(4, "0");
  return (a + b).slice(0, 12);
}

export const DEMO_AUDIT_SAMPLE = AUDIT_ENTRIES_RAW.map((e, i) => {
  const id = `AUD-BANK-${String(900000 - i).padStart(6, "0")}`;
  const hash = _mockHash(`${id}|${e.ts}|${e.actor}|${e.action}|${e.entityRef}`);
  // Chain backwards: newest entry's prevHash points to the next (older) entry.
  const prevSeed = AUDIT_ENTRIES_RAW[i + 1]
    ? `AUD-BANK-${String(900000 - (i + 1)).padStart(6, "0")}|${AUDIT_ENTRIES_RAW[i + 1].ts}|${AUDIT_ENTRIES_RAW[i + 1].actor}|${AUDIT_ENTRIES_RAW[i + 1].action}|${AUDIT_ENTRIES_RAW[i + 1].entityRef}`
    : "genesis";
  const prevHash = prevSeed === "genesis" ? "000000000000" : _mockHash(prevSeed);
  // Legacy fields kept for the policy drawer audit excerpt that reads `target`.
  return { id, ...e, target: e.entityRef, hash, prevHash };
});

// ----- Reports library -----
export const DEMO_REPORTS = [
  { id: "RPT-BANK-APRA-Q1-2026", name: "APRA CPS 234 — Q1 2026", generatedAt: "01 Apr 2026 09:01 AEDT", generatedBy: "system", framework: "APRA CPS 234", sizeMb: 14, signed: true },
  { id: "RPT-BANK-BOARD-MAR-2026", name: "Board Quarterly Pack — Mar 2026", generatedAt: "10 Mar 2026 06:00 AEDT", generatedBy: "system", framework: "Internal", sizeMb: 2.1, signed: true },
  { id: "RPT-BANK-EUAIACT-Q1", name: "EU AI Act — Article 9 Evidence", generatedAt: "08 Mar 2026 11:22 AEDT", generatedBy: "P. Ravi", framework: "EU AI Act", sizeMb: 8.4, signed: true },
  { id: "RPT-BANK-COST-W11", name: "Cost-by-Vendor — Week 11", generatedAt: "11 Mar 2026 07:00 AEDT", generatedBy: "M. Tan", framework: "Internal", sizeMb: 0.8, signed: true },
];

// ----- Aggregate KPIs (derived) -----
export function computeDemoKpis() {
  const govAgents = DEMO_AGENTS.filter((a) => a.status !== "ungoverned").length;
  const totalSpend = DEMO_AGENTS.reduce((s, a) => s + a.monthlyCostAud, 0);
  const totalValue = DEMO_AGENTS.reduce((s, a) => s + a.monthlyValueAud, 0);
  const ratio = totalSpend ? totalValue / totalSpend : 0;
  const openHigh = DEMO_INCIDENTS.filter((i) => i.severity === "high" && i.status !== "closed").length;
  const violations = DEMO_POLICIES.reduce((s, p) => s + p.violations, 0);
  const ungoverned = DEMO_AGENTS.filter((a) => a.status === "ungoverned").length;
  const pendingApprovals = DEMO_REQUESTS.filter((r) => r.stage < 5).length;
  return {
    governedAgents: govAgents,
    totalAgents: DEMO_AGENTS.length,
    monthlySpendAud: totalSpend,
    monthlyValueAud: totalValue,
    roiRatio: ratio,
    openHighIncidents: openHigh,
    violations,
    ungoverned,
    pendingApprovals,
    avgComplianceCoverage: Math.round(
      DEMO_FRAMEWORKS.reduce((s, f) => s + f.coverage, 0) / DEMO_FRAMEWORKS.length,
    ),
  };
}

// ----- Department-level rollups (for CEO / CAIO commercial views) -----
export function computeDepartmentRollups() {
  const map = new Map();
  for (const a of DEMO_AGENTS) {
    if (a.status === "ungoverned") continue;
    const entry = map.get(a.department) || {
      department: a.department,
      agents: 0,
      spendAud: 0,
      valueAud: 0,
      highRisk: 0,
    };
    entry.agents += 1;
    entry.spendAud += a.monthlyCostAud;
    entry.valueAud += a.monthlyValueAud;
    if (a.riskBand === "high") entry.highRisk += 1;
    map.set(a.department, entry);
  }
  return Array.from(map.values())
    .map((e) => ({ ...e, roi: e.spendAud ? e.valueAud / e.spendAud : 0 }))
    .sort((a, b) => b.valueAud - a.valueAud);
}

// ----- Vendor-level rollups (CAIO + CEO vendor split) -----
export function computeVendorRollups() {
  return DEMO_VENDORS.map((v) => {
    const agents = DEMO_AGENTS.filter((a) => a.vendorId === v.id);
    const spend = agents.reduce((s, a) => s + a.monthlyCostAud, 0);
    const value = agents.reduce((s, a) => s + a.monthlyValueAud, 0);
    return {
      ...v,
      agents: agents.length,
      spendAud: spend,
      valueAud: value,
      roi: spend ? value / spend : 0,
    };
  }).sort((a, b) => b.spendAud - a.spendAud);
}

// ----- 12-month trend (mocked, deterministic) for boardroom narrative -----
export function computeTrend12m() {
  const labels = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
  return labels.map((m, i) => {
    const spend = 380000 + i * 24000 + ((i * 7919) % 18000);
    const value = spend * (1.4 + i * 0.07);
    return { month: m, spendAud: spend, valueAud: Math.round(value) };
  });
}

// ============================================================
// COST OPTIMISATION (S18) · BENCHMARKING (S19) · VENDOR (S20) ·
// REPORT PACKS (S22) · SCHEDULED (S24) · USERS (S29)
// All data below is Acme Bank-specific (banking domain values).
// ============================================================

// 12-week AI spend trend (AUD).
// Anchored so the most-recent week (W22) sums to ~A$141k = vendor monthly
// total ($610.8k) divided by 4.33 weeks/month. Earlier weeks ramp from
// pilot scale (~A$92k/wk) reflecting the production cut-over in Jan 2026.
export const DEMO_COST_TREND = (() => {
  const weeks = ["W11","W12","W13","W14","W15","W16","W17","W18","W19","W20","W21","W22"];
  return weeks.map((w, i) => ({
    week: w,
    // Inference dominates: ~80% of spend, growing as agents go live.
    inference:  68000 + i * 3600 + (i % 3) * 800,
    embedding: 12400 + i *  520 + (i % 4) * 360,
    rag:        7800 + i *  240 + (i % 2) * 180,
    egress:     2700 + i *   80,
  }));
})();

// Identified cost-savings opportunities, sized as ~6% of annualised spend
// (A$7.3M ARR → A$430k addressable savings). Each item is routable into a
// deployment change request.
export const DEMO_COST_OPPORTUNITIES = [
  { id: "COST-BANK-0021", title: "Route Contact Centre Copilot summarisation through Claude 3.5 Haiku", agentId: "AGT-BANK-010", agentName: "Contact Centre Copilot",            estSavingsAud: 148000, qualityImpact: "-0.4% answer quality", confidence: "high",   effort: "low",  owner: "Marcus Tan",  status: "open" },
  { id: "COST-BANK-0020", title: "Cache repeat AML adverse-media lookups (7-day TTL)",                  agentId: "AGT-BANK-006", agentName: "AML Adverse Media Screening Agent", estSavingsAud:  84000, qualityImpact: "no impact",        confidence: "high",   effort: "low",  owner: "Khalid Hassan", status: "open" },
  { id: "COST-BANK-0019", title: "Right-size context window on Mortgage Doc Extraction (96k → 32k)",   agentId: "AGT-BANK-004", agentName: "Mortgage Document Extraction Agent",estSavingsAud:  62000, qualityImpact: "+0.1% accuracy",   confidence: "med",    effort: "med",  owner: "Aroha Wright", status: "in-review" },
  { id: "COST-BANK-0018", title: "Move SAR drafting from Claude Opus → Sonnet 4.5",                   agentId: "AGT-BANK-005", agentName: "Fraud Case Summarisation Agent",     estSavingsAud:  48000, qualityImpact: "needs A/B",        confidence: "med",    effort: "med",  owner: "Wei Lin Soo", status: "open" },
  { id: "COST-BANK-0017", title: "Move Treasury Narrative drafting to Bedrock cross-region quotas",   agentId: "AGT-BANK-013", agentName: "Treasury Narrative Drafter",         estSavingsAud:  24000, qualityImpact: "no impact",        confidence: "high",   effort: "low",  owner: "Jordan Patel", status: "approved" },
  { id: "COST-BANK-0016", title: "Switch Branch Advisor RAG embeddings to text-embedding-3-small",     agentId: "AGT-BANK-009", agentName: "Branch Advisor Assist",              estSavingsAud:  18000, qualityImpact: "-0.2% recall",     confidence: "med",    effort: "low",  owner: "Aroha Wright", status: "open" },
];

// Department spend rollup — derived from the agent-level monthly burn,
// grouped by business unit (label "department" is kept in the UI for
// historical reasons). Stable order: descending spend.
export const DEMO_COST_BY_DEPARTMENT = burnByBusinessUnit(DEMO_AGENTS);

// Peer benchmark data for the Benchmarking page.
// All values were calibrated against published 2025-2026 industry data
// (Gartner CIO AI Spend Surveys, Capgemini World Retail Banking Report,
// McKinsey FS AI ROI benchmarks, APRA prudential AI position papers).
export const DEMO_BENCHMARK = {
  peerGroup: "ANZ Banking Tier-1 — 12 banks anonymised",
  refreshedAt: "01 Mar 2026",
  metrics: [
    { metric: "Governed AI agents in production",      unit: "count",   ours: 14,    p25:  4,    p50:  9,    p75: 18,    p90: 26,    direction: "up" },
    { metric: "AI spend % of digital budget",          unit: "%",       ours: 5.8,   p25: 2.6,   p50: 4.1,   p75: 6.3,   p90: 8.4,   direction: "neutral" },
    { metric: "AI ROI multiple (3-yr realised)",       unit: "x",       ours: 3.4,   p25: 1.6,   p50: 2.4,   p75: 3.1,   p90: 4.2,   direction: "up" },
    { metric: "Median time-to-approval",               unit: "hrs",     ours: 18,    p25: 96,    p50: 64,    p75: 36,    p90: 12,    direction: "down" },
    { metric: "Hallucination rate (governed)",         unit: "%",       ours: 1.8,   p25: 4.6,   p50: 3.2,   p75: 2.1,   p90: 1.3,   direction: "down" },
    { metric: "Policy violations / 1k requests",       unit: "ratio",   ours: 0.9,   p25: 3.8,   p50: 2.1,   p75: 1.2,   p90: 0.6,   direction: "down" },
    { metric: "AU sovereign residency coverage",       unit: "%",       ours: 100,   p25: 78,    p50: 88,    p75: 96,    p90: 100,   direction: "up" },
    { metric: "Frameworks evidenced (APRA + global)",  unit: "count",   ours: 7,     p25: 2,     p50: 4,     p75: 5,     p90: 6,     direction: "up" },
  ],
};

// Integration Hub — extra fields layered on top of DEMO_VENDORS.
export const DEMO_VENDOR_DETAILS = {
  openai:      { models: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo"],      residency: "ap-southeast-2 (Sovereign EA)", connectedSince: "12 Jun 2025", contractTier: "Enterprise", msa: "Signed", mfaRequired: true },
  anthropic:   { models: ["claude-3.5-sonnet", "claude-3-haiku"],       residency: "ap-southeast-2",                 connectedSince: "04 Aug 2025", contractTier: "Enterprise", msa: "Signed", mfaRequired: true },
  bedrock:     { models: ["claude-3-sonnet", "llama-3.1-70b", "titan"], residency: "ap-southeast-2",                 connectedSince: "11 Mar 2025", contractTier: "AWS MSA",    msa: "Signed", mfaRequired: true },
  vertex:      { models: ["gemini-1.5-pro", "gemini-1.5-flash"],        residency: "australia-southeast1",           connectedSince: "21 Oct 2025", contractTier: "GCP MSA",    msa: "Signed", mfaRequired: true },
  "azure-oai": { models: ["gpt-4o", "gpt-4o-mini"],                     residency: "Australia East",                 connectedSince: "08 Jan 2025", contractTier: "EA",         msa: "Signed", mfaRequired: true },
  watsonx:     { models: ["granite-13b", "granite-3-instruct"],         residency: "ap-southeast-2",                 connectedSince: "02 Feb 2026", contractTier: "Enterprise", msa: "Signed", mfaRequired: true },
  copilot:     { models: ["m365-copilot"],                              residency: "Australia East",                 connectedSince: "15 Sep 2025", contractTier: "E5",         msa: "EA",     mfaRequired: true },
  "gh-copilot":{ models: ["copilot-chat"],                              residency: "Multi-region (US fallback)",     connectedSince: "06 May 2025", contractTier: "Enterprise", msa: "Signed", mfaRequired: true },
  mistral:     { models: ["mistral-large", "mistral-small"],            residency: "eu-west-3 (residency exception)",connectedSince: "18 Nov 2025", contractTier: "Pilot",      msa: "MSA-LE", mfaRequired: true },
  cohere:      { models: ["command-r-plus", "embed-multilingual"],      residency: "ca-central-1 (residency exception)", connectedSince: "27 Dec 2025", contractTier: "Pilot",  msa: "MSA-LE", mfaRequired: true },
};

// Available vendor connectors not yet installed (Integration Hub catalogue).
export const DEMO_VENDOR_CATALOGUE = [
  { id: "perplexity",   name: "Perplexity Enterprise",  category: "LLM",       residency: "US only",      msa: "Not signed",         availability: "blocked",   reason: "Fails AU residency policy POL-BANK-101." },
  { id: "writer",       name: "Writer Enterprise",      category: "LLM",       residency: "ap-southeast-2", msa: "MSA in review",     availability: "available", reason: "Awaiting vendor risk assessment by IT Security." },
  { id: "elevenlabs",   name: "ElevenLabs",             category: "Voice AI",  residency: "us-east-1",    msa: "MSA-LE",             availability: "restricted", reason: "Limited to non-customer voice (training assets only)." },
  { id: "stability",    name: "Stability AI",           category: "Image",     residency: "us-east-1",    msa: "Not signed",         availability: "available", reason: "Marketing use only; vendor review queued for Apr 2026." },
  { id: "deepseek",     name: "DeepSeek",               category: "LLM",       residency: "cn-north-1",   msa: "Not signed",         availability: "blocked",   reason: "China region — fails APRA CPS 234." },
];

// Pre-built report packs (templates one-click into the library).
export const DEMO_REPORT_PACKS = [
  { id: "PACK-BANK-APRA",   title: "APRA CPS 234 — Quarterly Evidence",     framework: "APRA CPS 234",    description: "All controls + supporting evidence, signed and timestamped for the AU regulator.",         estPages: 38, lastUsed: "01 Apr 2026", owner: "Priya Ravi"  },
  { id: "PACK-BANK-CPS230", title: "APRA CPS 230 — Operational Resilience", framework: "APRA CPS 230",    description: "Critical-operations mapping, third-party register, business continuity tests.",            estPages: 28, lastUsed: "12 Mar 2026", owner: "Marcus Tan" },
  { id: "PACK-BANK-ASIC",   title: "ASIC RG 271 — Conduct & Complaints",    framework: "ASIC RG 271",     description: "Conduct-risk dashboard, complaints theming, vulnerable-customer outcomes.",                estPages: 22, lastUsed: "28 Feb 2026", owner: "Priya Ravi" },
  { id: "PACK-BANK-EUAI",   title: "EU AI Act — High-Risk System Pack",     framework: "EU AI Act",        description: "Article 9 risk mgmt, Article 14 human oversight, Article 15 accuracy & robustness.",      estPages: 44, lastUsed: "08 Mar 2026", owner: "Priya Ravi" },
  { id: "PACK-BANK-ISO",    title: "ISO 42001 — Management System",         framework: "ISO 42001",        description: "AI management system clause-by-clause evidence for SOC 2 + ISO surveillance auditors.",  estPages: 34, lastUsed: "15 Feb 2026", owner: "Wei Lin Soo" },
  { id: "PACK-BANK-NIST",   title: "NIST AI RMF 1.0",                       framework: "NIST AI RMF",      description: "Govern / Map / Measure / Manage function mapping with control evidence.",                estPages: 26, lastUsed: "20 Jan 2026", owner: "Wei Lin Soo" },
  { id: "PACK-BANK-PRIV",   title: "Privacy Act AU — Annual Privacy Return",framework: "Privacy Act (AU)", description: "APP 1–13 evidence summary + near-miss register for the OAIC annual return.",             estPages: 18, lastUsed: "01 Jul 2025", owner: "Sasha Nair" },
  { id: "PACK-BANK-AUET",   title: "AU AI Ethics Principles",               framework: "AU AI Ethics",     description: "Eight-principle self-attestation for Federal procurement & state government RFPs.",      estPages: 14, lastUsed: "06 Feb 2026", owner: "Priya Ravi" },
];

// Scheduled reports — recurring deliveries.
export const DEMO_SCHEDULED_REPORTS = [
  { id: "SCH-BANK-001", name: "APRA Q1 Evidence Pack",         frequency: "Quarterly · Day 1 09:00 AEDT", nextRun: "01 Apr 2026 09:00 AEDT", recipients: ["CRO", "Head of Compliance"],                channel: "Email + SFTP", lastStatus: "delivered", owner: "Priya Ravi" },
  { id: "SCH-BANK-002", name: "Weekly Cost-by-Vendor Digest",   frequency: "Weekly · Mon 06:00 AEDT",      nextRun: "16 Mar 2026 06:00 AEDT", recipients: ["CIO", "FinOps"],                            channel: "Email + Slack", lastStatus: "delivered", owner: "Marcus Tan" },
  { id: "SCH-BANK-003", name: "Daily HIL Queue Backlog",        frequency: "Daily · 08:00 AEDT",           nextRun: "15 Mar 2026 08:00 AEDT", recipients: ["CAIO", "CRO", "BU Heads"],                  channel: "Email",         lastStatus: "delivered", owner: "Jordan Patel" },
  { id: "SCH-BANK-004", name: "Monthly Board Pack — AI Estate", frequency: "Monthly · Last business day",  nextRun: "31 Mar 2026 17:00 AEDT", recipients: ["CEO", "Board Risk Committee"],              channel: "Email",         lastStatus: "scheduled",  owner: "Jordan Patel" },
  { id: "SCH-BANK-005", name: "Incident Register — Open Items", frequency: "Daily · 17:00 AEDT",           nextRun: "14 Mar 2026 17:00 AEDT", recipients: ["IT Sec", "Risk Operations"],                channel: "Email + Teams", lastStatus: "delivered", owner: "Khalid Hassan" },
  { id: "SCH-BANK-006", name: "Vendor Allow-list Re-attest",    frequency: "Quarterly · Day 15 11:00 AEDT", nextRun: "15 Apr 2026 11:00 AEDT", recipients: ["IT Sec", "Procurement"],                   channel: "Email",         lastStatus: "scheduled",  owner: "Khalid Hassan" },
];

// Users & Roles — Acme Bank operational + read-only personas.
export const DEMO_USERS = [
  { id: "U-BANK-001", name: "Jordan Patel",      email: "jordan.patel@acmebank.com.au",    role: "CAIO",       department: "AI Office",         status: "active", mfa: true,  lastLogin: "14 Mar 2026 09:11 AEDT" },
  { id: "U-BANK-002", name: "Marcus Tan",        email: "marcus.tan@acmebank.com.au",      role: "CIO",        department: "Technology",        status: "active", mfa: true,  lastLogin: "14 Mar 2026 08:33 AEDT" },
  { id: "U-BANK-003", name: "Sasha Nair",        email: "sasha.nair@acmebank.com.au",      role: "CRO",        department: "Risk & Compliance", status: "active", mfa: true,  lastLogin: "14 Mar 2026 08:58 AEDT" },
  { id: "U-BANK-004", name: "Eleanor Whitcombe", email: "eleanor.whitcombe@acmebank.com.au",role:"CEO",         department: "Executive",          status: "active", mfa: true,  lastLogin: "13 Mar 2026 18:42 AEDT" },
  { id: "U-BANK-005", name: "Khalid Hassan",     email: "khalid.hassan@acmebank.com.au",   role: "IT Security",department: "Information Security",status:"active", mfa: true,  lastLogin: "14 Mar 2026 09:14 AEDT" },
  { id: "U-BANK-006", name: "Priya Ravi",        email: "priya.ravi@acmebank.com.au",      role: "Legal",      department: "Legal & Compliance", status: "active", mfa: true,  lastLogin: "13 Mar 2026 17:50 AEDT" },
  { id: "U-BANK-007", name: "Wei Lin Soo",       email: "weilin.soo@acmebank.com.au",      role: "DS Lead",    department: "Data Science",       status: "active", mfa: true,  lastLogin: "13 Mar 2026 17:40 AEDT" },
  { id: "U-BANK-008", name: "Aroha Wright",      email: "aroha.wright@acmebank.com.au",    role: "BU Head",    department: "Retail Banking",     status: "active", mfa: true,  lastLogin: "13 Mar 2026 17:20 AEDT" },
  { id: "U-BANK-009", name: "Hadley Brooks",     email: "hadley.brooks@acmebank.com.au",   role: "Auditor",    department: "Internal Audit",     status: "active", mfa: true,  lastLogin: "13 Mar 2026 12:08 AEDT" },
  { id: "U-BANK-010", name: "Olivia Strand",     email: "olivia.strand@acmebank.com.au",   role: "Exec Viewer",department: "Executive",          status: "active", mfa: true,  lastLogin: "12 Mar 2026 19:30 AEDT" },
  { id: "U-BANK-011", name: "Ravi Chen",         email: "ravi.chen@acmebank.com.au",       role: "BU Head",    department: "Business Banking",   status: "active", mfa: true,  lastLogin: "13 Mar 2026 16:01 AEDT" },
  { id: "U-BANK-012", name: "Lulu Okafor",       email: "lulu.okafor@acmebank.com.au",     role: "BU Head",    department: "Payments",           status: "active", mfa: true,  lastLogin: "13 Mar 2026 15:42 AEDT" },
  { id: "U-BANK-013", name: "Tarsha Berg",       email: "tarsha.berg@acmebank.com.au",     role: "DS Lead",    department: "Data Science",       status: "invited", mfa:false, lastLogin: "—" },
  { id: "U-BANK-014", name: "Nadira Kapoor",     email: "nadira.kapoor@acmebank.com.au",   role: "BU Head",    department: "Treasury",           status: "active", mfa: true,  lastLogin: "12 Mar 2026 11:18 AEDT" },
  { id: "U-BANK-015", name: "Francis Dubois",    email: "francis.dubois@acmebank.com.au",  role: "Auditor",    department: "Internal Audit",     status: "disabled",mfa: false, lastLogin: "06 Feb 2026 10:11 AEDT" },
];

// ============================================================
const ACME_TENANT_BUNDLE = {
  tenant: DEMO_TENANT,
  vendors: DEMO_VENDORS,
  vendorDetails: DEMO_VENDOR_DETAILS,
  vendorCatalogue: DEMO_VENDOR_CATALOGUE,
  agents: DEMO_AGENTS,
  opportunities: DEMO_OPPORTUNITIES,
  policies: DEMO_POLICIES,
  incidents: DEMO_INCIDENTS,
  hilQueue: DEMO_HIL_QUEUE,
  driftSignals: DEMO_DRIFT_SIGNALS,
  requests: DEMO_REQUESTS,
  frameworks: DEMO_FRAMEWORKS,
  auditEntries: DEMO_AUDIT_SAMPLE,
  reports: DEMO_REPORTS,
  reportPacks: DEMO_REPORT_PACKS,
  scheduledReports: DEMO_SCHEDULED_REPORTS,
  costTrend: DEMO_COST_TREND,
  costOpportunities: DEMO_COST_OPPORTUNITIES,
  costByDepartment: DEMO_COST_BY_DEPARTMENT,
  benchmark: DEMO_BENCHMARK,
  users: DEMO_USERS,
  kpis: computeDemoKpis(),
  departmentRollups: computeDepartmentRollups(),
  vendorRollups: computeVendorRollups(),
  vendorBurnRollup: deriveVendorBurnRollup(DEMO_VENDORS, DEMO_AGENTS),
  topBurners: topBurners(DEMO_AGENTS, 5),
  trend12m: computeTrend12m(),
  isEmpty: false,
};

// Northwind Insurance bundle (parallel insurance-flavoured tenant) is
// assembled in its own module so this file stays focused on Acme Bank.

export const DEMO_TENANT_REGISTRY = {
  "acme-bank": {
    id: "acme-bank",
    label: "Acme Bank",
    subtitle: "Banking & Financial Services · AU Sydney",
    bundle: ACME_TENANT_BUNDLE,
  },
  "northwind-insurance": {
    id: "northwind-insurance",
    label: "Northwind Insurance",
    subtitle: "General & Life Insurance · AU Melbourne",
    bundle: NORTHWIND_TENANT_BUNDLE,
  },
};

export const DEFAULT_DEMO_TENANT_ID = "acme-bank";

export function getDemoTenantBundle(tenantId = DEFAULT_DEMO_TENANT_ID) {
  return (
    DEMO_TENANT_REGISTRY[tenantId]?.bundle ||
    DEMO_TENANT_REGISTRY[DEFAULT_DEMO_TENANT_ID].bundle
  );
}

export function listDemoTenants() {
  return Object.values(DEMO_TENANT_REGISTRY).map((t) => ({
    id: t.id,
    label: t.label,
    subtitle: t.subtitle,
  }));
}
